import { useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useAttributes, useBulkImport, useCategories, useCollections, useCreateProduct, useCreateVariant,
  useAddCertificateAttachment, useAddProductImage, useCertificateAttachments,
  useDeleteCertificateAttachment, useDeleteProductImage, useDeleteVariants, useProduct,
  useSetCertificateRules, useSetPrimaryProductImage, useSetProductPublished,
  useUpdateProduct, useUpdateVariant, openCertificateAttachment,
} from '../../api/catalog';
import type {
  AttributeGroup, AttributeValue, BulkImportBody, BulkResult, BulkVariantBody,
  CertificateAttachment, CertificateRuleBody, CertificateRuleRow, ProductDetail as Product, VariantRow,
} from '../../api/catalog';
import { MultiSelect } from '../../ui/MultiSelect';
import { ImagePanel } from './ImagePanel';
import { isCodePrefix } from './code-prefix';
import { useCountries } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { describeError, type FieldMap } from '../../api/errors';
import { useSession } from '../../auth/session';
import {
  Alert, Button, Chip, Field, Modal, Money, Note, PageHeader, Panel, Spinner, Table, type Column,
} from '../../ui';

/** A variant's own fields, labelled as the Variants table and "Add a variant" label them. */
const variantFields = (section: string): FieldMap => ({
  code: { label: 'Variant code', section, hint: 'use letters, digits, hyphens or underscores (3–64 characters)' },
  purityValueId: { label: 'Purity', section },
  metalColourValueId: { label: 'Metal colour', section },
  purity: { label: 'Purity', section },
  metalColour: { label: 'Metal colour', section },
  caratCode: { label: 'Centre-stone carat code', section, hint: 'e.g. 050 for 0.50ct' },
  stoneOriginValueId: { label: 'Stone origin', section },
  gemstoneValueId: { label: 'Stone type', section },
  stoneShapeValueId: { label: 'Stone shape', section },
  stoneCutValueId: { label: 'Stone cut', section },
  stoneColourValueId: { label: 'Stone colour', section },
  stoneClarityValueId: { label: 'Stone clarity', section },
  netMetalWeightG: { label: 'Net metal weight (g)', section, hint: 'e.g. 3.25' },
  grossWeightG: { label: 'Gross weight (g)', section, hint: 'e.g. 3.40' },
  totalCaratWeight: { label: 'Total carat weight', section, hint: 'e.g. 0.50' },
  listPrice: { label: 'List price', section, hint: 'e.g. 1250.00' },
});

/**
 * Every field this screen can send, by wire name, with the label and panel it
 * appears under — so a refusal reads "Code prefix is required in the Identity
 * section" rather than "Validation failed".
 */
const PRODUCT_FIELDS: FieldMap = {
  name: { label: 'Product name', section: 'Identity' },
  codePrefix: {
    label: 'Code prefix', section: 'Identity',
    hint: 'use 2–24 capital letters, digits and hyphens, e.g. RG-101',
  },
  description: { label: 'Description', section: 'Identity' },
  collectionId: { label: 'Collection', section: 'Identity' },
  categoryId: { label: 'Category', section: 'Identity' },
  metalTypeCode: { label: 'Metal type', section: 'Offered in' },
  offeredValueIds: { label: 'Offered-in values', section: 'Offered in' },
  hsCode: { label: 'HS tariff code', section: 'Customs & origin' },
  countryOfOrigin: { label: 'Country of origin', section: 'Customs & origin' },
  settingValueId: { label: 'Setting', section: 'Make & marking' },
  finishValueId: { label: 'Finish', section: 'Make & marking' },
  claspValueId: { label: 'Clasp', section: 'Make & marking' },
  backingValueId: { label: 'Earring backing', section: 'Make & marking' },
  hallmarkValueId: { label: 'Hallmark authority', section: 'Make & marking' },
  ...variantFields('Variants'),
};

/** The proposal grid posts `variants.<n>.<field>` — the row number is added by `describeError`. */
const PROPOSAL_FIELDS: FieldMap = {
  ...variantFields('Proposed variants'),
  code: { label: 'Supplier SKU', section: 'Proposed variants', hint: 'use letters, digits, hyphens or underscores (3–64 characters)' },
};

/**
 * A-09 — archetype 10: the definition that generates variants.
 *
 * CR-43 — a product is not one thing with one photograph. It is a definition:
 * the purities and metal colours it is offered in, and the centre-stone carats it
 * is cut for. Multiplying those three axes gives the variant matrix at the foot of
 * this screen, and price and all three weights hang off the variant rather than the
 * product — 18k is denser than 14k and a bigger stone needs a heavier setting, so
 * none of them can be held once for the design.
 *
 * Two things this screen deliberately does not have:
 *
 *   - **No quantity, availability, stock or lead time.** Made to order, always (D4).
 *   - **No minimum order quantity.** Removed by CR-07.
 *
 * And one thing it deliberately does not do: **derive a variant code.** The server
 * does that, from the prefix, purity, colour and carat. The grid below can carry a
 * SUPPLIED sku — a supplier who already has a name for the piece has the better one,
 * and 622 of the client's 731 rows collapse onto a duplicate if we insist on ours —
 * but a supplied code is a code the business already uses, not one invented here.
 *
 * Variants are authored in three moves: generate the combinations the axes imply,
 * strike out the ones Miraki does not make, price the rest. Generation is a PROPOSAL
 * and writes nothing; the save goes through A-11's `POST /catalog/import`, which
 * already does 731 rows in about seven seconds, rather than a second bulk path.
 *
 * Every price and every weight on this screen is a decimal string from the API to the
 * input and back. Nothing calls `Number()` on one (NF-04).
 */

/** The attribute groups this screen reads. Keys are the API's, not this screen's. */
const GROUP = {
  metalType: 'mtype',
  purity: 'purity',
  metalColour: 'mcol',
  carat: 'carat',
  setting: 'setting',
  finish: 'finish',
  clasp: 'clasp',
  backing: 'backing',
  hallmark: 'hallmark',
  /**
   * Stone — a variant-defining axis, the same standing as purity/metal
   * colour above. `gemstone` ('gemtype') is the MATERIAL: Emerald, Ruby,
   * Sapphire, Diamond. Deliberately a different key from `stoneShape`
   * ('shape'), whose own 'EM' code is emerald-CUT, a faceting style — not
   * the gemstone. The other three reuse the same global trade lists the
   * product-level stone panel already offers.
   */
  gemstone: 'gemtype',
  /**
   * Lab Grown or Natural — an ORIGIN, orthogonal to the material beside it and
   * its own axis rather than four more `gemtype` rows, because folding it in
   * would grow the material list by multiplication. Priced, not descriptive:
   * three of the client's six catalogue files ship both values and the origin
   * moves the price in every one, which is why it generates variants.
   */
  stoneOrigin: 'origin',
  stoneShape: 'shape',
  stoneCut: 'cut',
  stoneColour: 'scol',
  stoneClarity: 'clar',
} as const;

interface Draft {
  name: string;
  codePrefix: string;
  description: string;
  collectionId: string;
  categoryId: string;
  metalTypeCode: string;
  hsCode: string;
  countryOfOrigin: string;
  settingValueId: string;
  finishValueId: string;
  claspValueId: string;
  backingValueId: string;
  hallmarkValueId: string;
}

/** The three axes the matrix is built from. Client state — see the note at the foot. */
interface Axes {
  purityIds: string[];
  colourIds: string[];
  caratCodes: string[];
}

/**
 * Which Material/Shape/Cut/Colour/Clarity values this design is offered in —
 * Stone's own counterpart to `Axes`, kept as a separate block rather than
 * folded into the metal matrix above. Persisted (`productOfferedValueIds`)
 * and read directly by the retailer's own Stone dropdowns
 * (`shop.controller.ts`'s `stoneOptions`).
 *
 * These axes GENERATE, the same as the metal ones. The client's own catalogue
 * settles it: a tennis bracelet is nine metal frames times twenty-eight stone
 * specifications, and the price moves on both halves — so a stone axis that
 * only described the design would leave 243 of its 252 rows unpriceable.
 */
interface StoneAxes {
  stoneOriginIds: string[];
  gemstoneIds: string[];
  stoneShapeIds: string[];
  stoneCutIds: string[];
  stoneColourIds: string[];
  stoneClarityIds: string[];
}

const EMPTY_DRAFT: Draft = {
  name: '', codePrefix: '', description: '', collectionId: '', categoryId: '',
  metalTypeCode: '', hsCode: '', countryOfOrigin: '', settingValueId: '',
  finishValueId: '', claspValueId: '', backingValueId: '', hallmarkValueId: '',
};

const draftOf = (p: Product): Draft => ({
  name: p.name,
  codePrefix: p.codePrefix,
  description: p.description ?? '',
  collectionId: p.collectionId ?? '',
  categoryId: p.categoryId ?? '',
  metalTypeCode: p.metalTypeCode,
  hsCode: p.hsCode ?? '',
  countryOfOrigin: p.countryOfOrigin ?? '',
  settingValueId: p.settingValueId ?? '',
  finishValueId: p.finishValueId ?? '',
  claspValueId: p.claspValueId ?? '',
  backingValueId: p.backingValueId ?? '',
  hallmarkValueId: p.hallmarkValueId ?? '',
});

const nul = (s: string) => (s.trim() === '' ? null : s.trim());

function group(groups: AttributeGroup[] | undefined, key: string): AttributeGroup | undefined {
  return groups?.find((g) => g.key === key);
}

/**
 * CR-12 — only what the portal currently offers, and CR-45 — only what this metal
 * has. A karat is a gold measure; platinum is quoted in parts per thousand and has
 * no colour choice at all, so the same list cannot serve both.
 *
 * A value already carried by a saved variant stays visible in the matrix even after
 * it is switched off — deactivating does not delete — but it is not offered here for
 * anything new.
 */
function offered(g: AttributeGroup | undefined, metalTypeCode: string): AttributeValue[] {
  if (!g) return [];
  return g.values.filter(
    (v) => v.isActive && (v.metalTypeCode === null || v.metalTypeCode === metalTypeCode),
  );
}

const labelOf = (values: AttributeValue[], id: string | null): string | null =>
  values.find((v) => v.id === id)?.label ?? null;

/**
 * The bulk route resolves purity and metal colour by CODE, not by id — it holds
 * the portal's active values in memory and refuses an unknown or inactive code
 * by name. So the grid sends the code, not the uuid the single-row path takes.
 */
const codeOf = (values: AttributeValue[], id: string | null): string | null =>
  values.find((v) => v.id === id)?.code ?? null;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "a, b and c" — a short list read as a sentence. */
const listInWords = (items: readonly string[]) =>
  items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** The three weight figures, inline-edited the same way on every variant row. */
type WeightField = 'netMetalWeightG' | 'grossWeightG' | 'totalCaratWeight';
const WEIGHT_LABEL: Record<WeightField, string> = {
  netMetalWeightG: 'Net weight', grossWeightG: 'Gross weight', totalCaratWeight: 'Total carat',
};
const WEIGHT_FIELDS: readonly WeightField[] = ['netMetalWeightG', 'grossWeightG', 'totalCaratWeight'];

/**
 * List price plus the three weights — ONE common Edit/Save on a row now
 * covers all four, rather than list price saving on its own (blur) while
 * the other three shared a second trigger. Editing any one no longer
 * depends on any other already having a value.
 */
type EditableField = 'listPrice' | 'rpPrice' | WeightField;

/**
 * The same three figures, before a variant exists to hang them off —
 * `Proposal`'s own draft maps, keyed by candidate row rather than by variant
 * id. A distinct type from `WeightField` on purpose: these key the PROPOSAL
 * (`Proposal.netWeights` etc.), not a `VariantRow`.
 */
type ProposalWeightField = 'netWeights' | 'grossWeights' | 'totalCarats';
const PROPOSAL_WEIGHT_META: Record<ProposalWeightField, { label: string; unit: string; variantField: WeightField }> = {
  netWeights: { label: 'Net weight', unit: 'g', variantField: 'netMetalWeightG' },
  grossWeights: { label: 'Gross weight', unit: 'g', variantField: 'grossWeightG' },
  totalCarats: { label: 'Total carat', unit: 'ct', variantField: 'totalCaratWeight' },
};

// --------------------------------------------------------------- reading money

/**
 * `packages/money`'s own `MONEY_PATTERN`, restated rather than imported.
 *
 * That is a packaging fact, not a preference: `@miraki/money` ships CommonJS
 * with no `import` condition and Vite does not pre-bundle it, so a VALUE import
 * of it does not resolve in the browser at all — `screens/Shop.tsx` and
 * `screens/shop/Order.tsx` each record the same finding and each restate what
 * they need. A type-only import would be fine; this is not one. Keep it in step
 * with `money/src/index.ts`, because it is the definition of what this screen
 * will accept as a price.
 */
const MONEY_PATTERN = /^-?\d{1,15}(\.\d{1,4})?$/;

/**
 * `packages/contracts`'s own `zWeight` pattern, restated for the same
 * packaging reason as `MONEY_PATTERN` above — `@miraki/contracts` ships
 * CommonJS too. Unlike a price, a weight is never negative.
 */
const WEIGHT_PATTERN = /^\d{1,12}(\.\d{1,4})?$/;
const cleanWeight = (cell: string) => cell.replace(/[^0-9.]/g, '');
const isWeight = (v: string) => WEIGHT_PATTERN.test(v);

/**
 * A price at NUMERIC(19,4)'s scale, as an exact integer of ten-thousandths.
 *
 * The same `scaled()` approach `screens/shop/Order.tsx` and `screens/Shop.tsx`
 * already use, for the same reason: `Number('1250.0001')` is a float and a
 * float cannot hold that. COMPARISON ONLY — no arithmetic is done on the
 * result, nothing built from it is ever sent anywhere, and the string that came
 * in is the string that goes back out (NF-04).
 */
function scaledPrice(s: string): bigint {
  const negative = s.startsWith('-');
  const [whole = '0', frac = ''] = s.replace('-', '').split('.');
  const n = BigInt(whole || '0') * 10000n + BigInt((`${frac}0000`).slice(0, 4));
  return negative ? -n : n;
}

/**
 * The lowest and the highest of a set of prices, returned as the STRINGS that
 * came in.
 *
 * `Math.min(...prices.map(Number))` is what this replaces, and it was wrong
 * twice over: `Number` rounds past 2^53, and it drops the fourth decimal place
 * NUMERIC(19,4) is carefully keeping — so a group header could quote a figure
 * no row inside it agreed with.
 */
function priceSpan(amounts: readonly string[]): { low: string; high: string } | null {
  const valid = amounts.filter((a) => MONEY_PATTERN.test(a));
  const first = valid[0];
  if (first === undefined) return null;
  let low = first;
  let high = first;
  for (const a of valid) {
    if (scaledPrice(a) < scaledPrice(low)) low = a;
    if (scaledPrice(a) > scaledPrice(high)) high = a;
  }
  return { low, high };
}

/**
 * "2500.0000" -> "2500", "2500.5000" -> "2500.5", "2500.2500" -> "2500.25".
 *
 * The server always returns a price at NUMERIC(19,4)'s full scale, so an input
 * pre-filled from the API shows four decimal places even when nobody typed
 * them. Trailing zeros only — the value itself is untouched and never parsed
 * to a float (NF-04). Display formatting only: the string this produces is
 * what goes back on the wire unchanged, and the server round-trips it through
 * `money()` regardless of how many trailing zeros it carries.
 */
function trimAmount(s: string): string {
  const negative = s.startsWith('-');
  const body = negative ? s.slice(1) : s;
  const [whole = '0', frac = ''] = body.split('.');
  const trimmedFrac = frac.replace(/0+$/, '');
  const out = trimmedFrac ? `${whole}.${trimmedFrac}` : whole;
  return negative ? `-${out}` : out;
}

// ------------------------------------------------------------ CR-43 generation

/**
 * The nine fields that identify a variant, in the order a workbook lists them.
 *
 * Metal frame first, then stone specification — which is the shape of every one
 * of the client's six files. A paste of a price column follows this order, so it
 * is not an implementation detail: it is the contract with the spreadsheet.
 */
type AxisField =
  | 'purityValueId' | 'metalColourValueId' | 'caratCode'
  | 'stoneOriginValueId' | 'gemstoneValueId' | 'stoneShapeValueId'
  | 'stoneCutValueId' | 'stoneColourValueId' | 'stoneClarityValueId';

type Identity = Record<AxisField, string | null>;

const NO_IDENTITY: Identity = {
  purityValueId: null, metalColourValueId: null, caratCode: null,
  stoneOriginValueId: null, gemstoneValueId: null, stoneShapeValueId: null,
  stoneCutValueId: null, stoneColourValueId: null, stoneClarityValueId: null,
};

/** Purity x metal colour x carat — one METAL FRAME. */
const FRAME_FIELDS: readonly AxisField[] = ['purityValueId', 'metalColourValueId', 'caratCode'];
/** Origin x material x shape x cut x colour x clarity — one STONE SPECIFICATION. */
const STONE_FIELDS: readonly AxisField[] = [
  'stoneOriginValueId', 'gemstoneValueId', 'stoneShapeValueId',
  'stoneCutValueId', 'stoneColourValueId', 'stoneClarityValueId',
];
const ALL_FIELDS: readonly AxisField[] = [...FRAME_FIELDS, ...STONE_FIELDS];

const idKey = (i: Identity, fields: readonly AxisField[]) => fields.map((f) => i[f] ?? '-').join('/');

/**
 * A METAL SET — one purity in one colour, e.g. 10k Rose. The client works a
 * design one metal set at a time: generate 10k Rose, price it, create it, then
 * 10k Yellow. So generation is scoped to a set, not to the whole cross product,
 * and the same key groups the variants panel above.
 */
const metalSetKey = (i: Pick<Identity, 'purityValueId' | 'metalColourValueId'>) =>
  `${i.purityValueId ?? '-'}/${i.metalColourValueId ?? '-'}`;

/** One proposed combination. Identity only — price and SKU are the author's. */
interface Candidate extends Identity {
  key: string;
  frameKey: string;
}

/**
 * The author's working copy of the matrix. NOTHING here has been written.
 *
 * `notMade` is the pruning, and the word is chosen: a combination the author
 * removes is one Miraki DOES NOT MAKE. It is not unavailable, not out of stock
 * and not back-ordered — there is no stock anywhere in this product (D4), so
 * those three words would each describe a state that cannot exist. Platinum in
 * rose gold is not out of stock; it is not a thing.
 */
interface Proposal {
  rows: Candidate[];
  /** The metal set these rows were generated for, e.g. "10k · Rose". */
  metalSetLabel?: string;
  /** Row key to price, exactly as typed. A string, start to finish (NF-04). */
  prices: Readonly<Record<string, string>>;
  /** Row key to the SKU the business already uses. Blank means "derive it". */
  skus: Readonly<Record<string, string>>;
  /**
   * Row key to net weight / gross weight / total carat, the same shape and
   * the same "Apply to every" pattern as `prices` — a common default typed
   * once, then overridable per row before Create, and freely editable per
   * variant afterwards (the Variance block's own inline Edit/Save). Blank
   * means "leave the field unset", the same as an empty price leaves a row
   * un-created rather than created at zero.
   */
  netWeights: Readonly<Record<string, string>>;
  grossWeights: Readonly<Record<string, string>>;
  totalCarats: Readonly<Record<string, string>>;
  notMade: ReadonlySet<string>;
}

/** An axis with nothing chosen contributes one null — it does not apply here. */
const orNone = (xs: readonly string[]): Array<string | null> => (xs.length ? [...xs] : [null]);

/** The shape of the matrix the axes imply: the total, and how it factors. */
interface MatrixSize {
  /** Every combination the nine axes imply. */
  readonly total: number;
  /** Purity × metal colour × carat. */
  readonly metalFrames: number;
  /** Origin × material × shape × cut × stone colour × clarity. */
  readonly stoneSpecs: number;
  readonly purities: number;
  readonly colours: number;
  readonly carats: number;
}

/** An axis with nothing chosen still contributes one row: it does not apply. */
const axisSize = (xs: readonly string[]): number => Math.max(xs.length, 1);

/**
 * How many rows the current selection implies, WITHOUT building them.
 *
 * Asked before generating, because the multiplication is the thing a person
 * most wants to see before they commit to a 252-row grid — and because an axis
 * chosen by accident is a thousand rows, not a mistake you notice afterwards.
 *
 * THE ONE PLACE THIS SCREEN MULTIPLIES THE AXES OUT. It used to happen twice:
 * the page subtitle multiplied the metal frame on its own while the Generate
 * panel multiplied all nine axes, and the two copies diverged twice — once when
 * the stone axes arrived, again when origin did — so one screen could read
 * "9 variants" in its header and "180 candidates" in its panel. Both readings
 * now come from here and from `matrixInWords` below, so an axis added tomorrow
 * moves both or neither. Add it to this function and to nothing else.
 */
function matrixSize(axes: Axes, stone: StoneAxes): MatrixSize {
  const purities = axisSize(axes.purityIds);
  const colours = axisSize(axes.colourIds);
  const carats = axisSize(axes.caratCodes);
  const metalFrames = purities * colours * carats;
  const stoneSpecs = [
    stone.stoneOriginIds, stone.gemstoneIds, stone.stoneShapeIds,
    stone.stoneCutIds, stone.stoneColourIds, stone.stoneClarityIds,
  ].reduce((n, xs) => n * axisSize(xs), 1);
  return { total: metalFrames * stoneSpecs, metalFrames, stoneSpecs, purities, colours, carats };
}

/**
 * The same multiplication in words, for every place that shows it — the page
 * subtitle and the Generate panel both read this rather than writing their own.
 *
 * The stone half is named as a count rather than spelled out axis by axis: six
 * more factors in a page subtitle is not a sentence anybody reads, and a stone
 * axis added later is absorbed here instead of being forgotten in one of the
 * two places. The clause is dropped entirely when there is no stone axis at
 * all, because "each in 1 stone specification" says nothing.
 */
function matrixInWords(m: MatrixSize): string {
  const frame = `${plural(m.purities, 'purity', 'purities')} × ${plural(m.colours, 'colour', 'colours')}`
    + ` × ${plural(m.carats, 'carat', 'carats')}`;
  return m.stoneSpecs === 1
    ? frame
    : `${frame}, each in ${plural(m.stoneSpecs, 'stone specification', 'stone specifications')}`;
}

/**
 * The cross product of the nine axes.
 *
 * This is the PROPOSAL and it writes nothing — `zGenerateVariants` says so in
 * as many words, and this function takes exactly that schema's field set so the
 * day the route lands it is a swap and not a rewrite. Generation that wrote rows
 * would make "show me what this would be" indistinguishable from "create 252
 * products".
 */
function generate(axes: Axes, stone: StoneAxes): Candidate[] {
  const plan: Array<[AxisField, Array<string | null>]> = [
    ['purityValueId', orNone(axes.purityIds)],
    ['metalColourValueId', orNone(axes.colourIds)],
    ['caratCode', orNone(axes.caratCodes)],
    ['stoneOriginValueId', orNone(stone.stoneOriginIds)],
    ['gemstoneValueId', orNone(stone.gemstoneIds)],
    ['stoneShapeValueId', orNone(stone.stoneShapeIds)],
    ['stoneCutValueId', orNone(stone.stoneCutIds)],
    ['stoneColourValueId', orNone(stone.stoneColourIds)],
    ['stoneClarityValueId', orNone(stone.stoneClarityIds)],
  ];
  let acc: Identity[] = [NO_IDENTITY];
  for (const [field, values] of plan) {
    const next: Identity[] = [];
    for (const row of acc) {
      for (const v of values) {
        const merged: Identity = { ...row };
        merged[field] = v;
        next.push(merged);
      }
    }
    acc = next;
  }
  return acc.map((i) => ({ ...i, key: idKey(i, ALL_FIELDS), frameKey: idKey(i, FRAME_FIELDS) }));
}

/** The same key a candidate carries, for a variant that already exists. */
const variantKey = (v: VariantRow): string => idKey({
  purityValueId: v.purityValueId,
  metalColourValueId: v.metalColourValueId,
  caratCode: v.caratCode,
  stoneOriginValueId: v.stoneOriginValueId ?? null,
  gemstoneValueId: v.gemstoneValueId,
  stoneShapeValueId: v.stoneShapeValueId,
  stoneCutValueId: v.stoneCutValueId,
  stoneColourValueId: v.stoneColourValueId,
  stoneClarityValueId: v.stoneClarityValueId,
}, ALL_FIELDS);

/**
 * Stone identity, in the metafield seam.
 *
 * The stone axes as CODES, the same shape `purity` and `metalColour` travel in —
 * a spreadsheet carries 'VVS1', not a uuid, and the server resolves it.
 *
 * These were briefly smuggled through the `attributes` metafield because
 * `zBulkVariant` had no stone fields. That was wrong in a way worth recording:
 * the retailer screen builds its stone dropdowns from the COLUMNS, so variants
 * created that way had no stone at all on the shop side, and the derived code —
 * which also reads them — collapsed the client's 200-row Colette onto about ten
 * names and collided. The contract now carries them.
 */
const STONE_WIRE: ReadonlyArray<[AxisField, keyof AxisLabels, string]> = [
  ['stoneOriginValueId', 'origin', 'stoneOrigin'],
  ['gemstoneValueId', 'gemstone', 'gemstone'],
  ['stoneShapeValueId', 'shape', 'stoneShape'],
  ['stoneCutValueId', 'cut', 'stoneCut'],
  ['stoneColourValueId', 'stoneColour', 'stoneColour'],
  ['stoneClarityValueId', 'clarity', 'stoneClarity'],
];

function stoneCodes(c: Candidate, labels: AxisLabels): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, list, wire] of STONE_WIRE) {
    const code = codeOf(labels[list], c[field]);
    if (code !== null) out[wire] = code;
  }
  return out;
}

/** The option lists the grid renders its labels from. */
interface AxisLabels {
  purity: AttributeValue[];
  colour: AttributeValue[];
  origin: AttributeValue[];
  gemstone: AttributeValue[];
  shape: AttributeValue[];
  cut: AttributeValue[];
  stoneColour: AttributeValue[];
  clarity: AttributeValue[];
}

const frameLabel = (c: Candidate, L: AxisLabels) => [
  labelOf(L.purity, c.purityValueId) ?? 'No purity',
  labelOf(L.colour, c.metalColourValueId) ?? 'No colour',
  c.caratCode ?? 'No carat',
].join(' \u00b7 ');

const stoneLabel = (c: Candidate, L: AxisLabels) => [
  labelOf(L.origin, c.stoneOriginValueId),
  labelOf(L.gemstone, c.gemstoneValueId),
  labelOf(L.shape, c.stoneShapeValueId),
  labelOf(L.cut, c.stoneCutValueId),
  labelOf(L.stoneColour, c.stoneColourValueId),
  labelOf(L.clarity, c.stoneClarityValueId),
].filter(Boolean).join(' \u00b7 ');

// ------------------------------------------------------- a column, from Excel

/**
 * 'GBP 1,250.00' becomes '1250.00'.
 *
 * STRING SURGERY, not parsing. The currency, the thousands separators and the
 * stray spaces a spreadsheet pastes are removed character by character, and what
 * survives is checked against `packages/money`'s own pattern. The value never
 * becomes a number — which is the entire reason this is not `parseFloat(cell)`,
 * and why a European '1.250,00' is REJECTED and reported rather than quietly
 * read as one and a quarter (NF-04).
 */
const cleanPrice = (cell: string) => cell.replace(/[^0-9.-]/g, '');
const isPrice = (v: string) => MONEY_PATTERN.test(v);

export function ProductDetail() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { can } = useSession();
  const isNew = code === 'new';

  const { data: product, isLoading, error } = useProduct(isNew ? undefined : code);
  const { data: groups } = useAttributes();
  const { data: collections } = useCollections();
  const { data: categories } = useCategories();
  const { data: countries } = useCountries();

  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteImage = useDeleteProductImage();
  const setPrimaryImage = useSetPrimaryProductImage();
  const addImage = useAddProductImage();
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);

  /**
   * Upload a batch ONE AT A TIME, in order.
   *
   * In parallel they would finish in whatever order the network decided, and
   * `sort_order` is assigned server-side from what is already there — so a
   * dropped set of five would land shuffled. Sequential also means a failure
   * names the file that failed and the rest still go.
   *
   * The caption defaults to the file name without its extension, which is what
   * the client's own folders carry and is better than nothing on the screen.
   */
  async function addMany(files: File[], colour: string | null) {
    setFailed(null);
    setUploading({ done: 0, total: files.length });
    const failures: string[] = [];
    for (const [i, file] of files.entries()) {
      try {
        await addImage.mutateAsync({
          productId: product!.id,
          file,
          colour,
          caption: file.name.replace(/\.[^.]+$/, ''),
        });
      } catch (e) {
        failures.push(`${file.name}: ${e instanceof Error ? e.message : 'failed'}`);
      }
      setUploading({ done: i + 1, total: files.length });
    }
    setUploading(null);
    if (failures.length) {
      setFailed(
        `${failures.length} of ${files.length} did not upload — ${failures.slice(0, 3).join('; ')}`
        + (failures.length > 3 ? ` and ${failures.length - 3} more.` : '.'),
      );
    }
  }
  const setPublished = useSetProductPublished();
  const bulkImport = useBulkImport();
  const saveRules = useSetCertificateRules();
  const certAttachments = useCertificateAttachments(isNew ? undefined : product?.id);
  const addCertAttachment = useAddCertificateAttachment();
  const deleteCertAttachment = useDeleteCertificateAttachment();
  const createVariant = useCreateVariant();
  const updateVariant = useUpdateVariant();
  const deleteVariants = useDeleteVariants();

  const [edits, setEdits] = useState<Partial<Draft> | null>(null);
  const [axesEdit, setAxesEdit] = useState<Axes | null>(null);
  const [stoneAxesEdit, setStoneAxesEdit] = useState<StoneAxes | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /** A-08's bulk Publish reports what it did in a banner — this is that same
   *  feedback, scoped to one product, so Publish here does not look silent. */
  const [banner, setBanner] = useState<string | null>(null);
  /**
   * List price, net weight, gross weight and total carat — ONE Edit/Save
   * covering all four, not list price saving on blur while the other three
   * shared a second trigger. Only one ROW edits at a time (`editingRow` is a
   * variant id, not a variant+field pair): a second Edit click switches the
   * target rather than opening a second set of boxes.
   */
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [fieldDrafts, setFieldDrafts] = useState<Record<EditableField, string>>({
    listPrice: '', rpPrice: '', netMetalWeightG: '', grossWeightG: '', totalCaratWeight: '',
  });
  const [rowSaving, setRowSaving] = useState(false);
  const [createPricedPending, setCreatePricedPending] = useState(false);
  /** Withdraw moved beside "Add a variant" (bulk) — variant ids, not candidate keys. */
  const [selectedVariants, setSelectedVariants] = useState<ReadonlySet<string>>(new Set());
  const [bulkWithdrawPending, setBulkWithdrawPending] = useState(false);
  /**
   * Prices typed against combinations that do not exist yet, by candidate key.
   *
   * A price is the one thing a variant cannot be created without, so entering
   * it on the row IS the act of proposing that row — no separate grid, no
   * second idea of what has been priced. Cleared when the rows land.
   */
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});
  /**
   * Net weight, gross weight and total carat typed against the same
   * not-yet-created rows, by the same candidate key — so a row is entered
   * whole, once, and created with all four rather than priced now and
   * edited later. Optional, unlike the price: they ride along with it.
   */
  const [draftWeights, setDraftWeights] = useState<Record<string, Partial<Record<WeightField, string>>>>({});
  const [creating, setCreating] = useState(false);
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set());
  /** CR-43 — the proposed matrix. Held here, written nowhere until Create. */
  const [proposal, setProposal] = useState<Proposal | null>(null);
  /** The metal set (purity + colour) the admin picked to generate; null = the next unfinished one. */
  const [chosenSet, setChosenSet] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<BulkResult | null>(null);

  const variants = useMemo(() => product?.variants ?? [], [product]);

  /**
   * `product.offeredValueIds` (CR-45/CR-12) is what "Save" actually persists —
   * see `catalog.service.ts`'s `productOfferedValue` table. A product saved
   * before that table existed carries none, so the fallback derives the same
   * set the old, ephemeral version of this screen used to compute on the fly:
   * whatever its real variants already prove it is offered in. The first Save
   * on such a product turns that fallback into a real, persisted answer.
   */
  const offeredIds = useMemo<string[]>(() => {
    if (product?.offeredValueIds.length) return product.offeredValueIds;
    const caratValues = group(groups, GROUP.carat)?.values ?? [];
    const ids = new Set<string>();
    for (const v of variants) {
      for (const id of [
        v.purityValueId, v.metalColourValueId, v.stoneOriginValueId ?? null, v.gemstoneValueId,
        v.stoneShapeValueId, v.stoneCutValueId, v.stoneColourValueId, v.stoneClarityValueId,
      ]) if (id) ids.add(id);
      const cv = v.caratCode ? caratValues.find((c) => c.code === v.caratCode) : undefined;
      if (cv) ids.add(cv.id);
    }
    return [...ids];
  }, [product, variants, groups]);

  /**
   * The axes default to what the design is actually offered in (`offeredIds`),
   * rather than to everything the portal offers. A product with two purities
   * on file should not open claiming it comes in five.
   */
  const savedAxes = useMemo<Axes>(() => {
    const set = new Set(offeredIds);
    const purityValues = group(groups, GROUP.purity)?.values ?? [];
    const colourValues = group(groups, GROUP.metalColour)?.values ?? [];
    const caratValues = group(groups, GROUP.carat)?.values ?? [];
    return {
      purityIds: purityValues.filter((v) => set.has(v.id)).map((v) => v.id),
      colourIds: colourValues.filter((v) => set.has(v.id)).map((v) => v.id),
      caratCodes: caratValues.filter((v) => set.has(v.id)).map((v) => v.code),
    };
  }, [offeredIds, groups]);

  /** Same idea as `savedAxes`, for the five Stone fields. */
  const savedStoneAxes = useMemo<StoneAxes>(() => {
    const set = new Set(offeredIds);
    const pick = (key: string) => (group(groups, key)?.values ?? []).filter((v) => set.has(v.id)).map((v) => v.id);
    return {
      stoneOriginIds: pick(GROUP.stoneOrigin),
      gemstoneIds: pick(GROUP.gemstone),
      stoneShapeIds: pick(GROUP.stoneShape),
      stoneCutIds: pick(GROUP.stoneCut),
      stoneColourIds: pick(GROUP.stoneColour),
      stoneClarityIds: pick(GROUP.stoneClarity),
    };
  }, [offeredIds, groups]);

  // `axesEdit`/`stoneAxesEdit` override the saved defaults the instant
  // either panel is touched — moved above the early returns below, with the
  // rest of this block, purely so the expensive part of it can be memoized
  // instead of rebuilding on every unrelated re-render of this screen (a
  // keystroke in the product name used to redo it too). `generate` itself
  // and everything it produces are untouched — this only stops recomputing
  // them for no reason.
  const axes: Axes = axesEdit ?? savedAxes;
  const stoneAxes: StoneAxes = stoneAxesEdit ?? savedStoneAxes;

  const allPurities = useMemo(() => group(groups, GROUP.purity)?.values ?? [], [groups]);
  const allColours = useMemo(() => group(groups, GROUP.metalColour)?.values ?? [], [groups]);

  const size = matrixSize(axes, stoneAxes);
  const candidateTotal = size.total;
  // A count. `zBulkImport` caps a submission at 5000 variants and the largest
  // real file is 252, so anything past this is an axis chosen by accident
  // rather than a catalog.
  const GENERATION_CAP = 2000;

  interface Cell {
    key: string;
    purityValueId: string | null;
    metalColourValueId: string | null;
    caratCode: string | null;
    stoneOriginValueId: string | null;
    gemstoneValueId: string | null;
    stoneShapeValueId: string | null;
    stoneCutValueId: string | null;
    stoneColourValueId: string | null;
    stoneClarityValueId: string | null;
    variants: VariantRow[];
  }
  type Row = Omit<Cell, 'variants'> & { variant: VariantRow | undefined };

  /**
   * The expensive part of this screen: the full cross product of up to nine
   * axes (up to `GENERATION_CAP` candidates), matched against what already
   * exists and grouped by purity/colour. This used to run unmemoized on
   * every render of a large, single-component screen, and `alreadyInCatalog`
   * called `generate` a SECOND time on top of `candidates` below it — so a
   * design near the cap regenerated its full candidate matrix twice, on
   * every keystroke anywhere on the page. Identity, matching and grouping
   * are byte-for-byte what they were; only the recomputation is gone.
   */
  const derived = useMemo(() => {
    const existingByKey = new Map<string, VariantRow>();
    for (const v of variants) existingByKey.set(variantKey(v), v);

    /*
     * Built from `generate` — the SAME nine-axis cross product the Generate
     * panel counts from — rather than a second, shorter idea of what a
     * combination is. `matrixSize` already carries the warning that this
     * multiplication must happen in exactly one place.
     */
    const candidates = candidateTotal <= GENERATION_CAP ? generate(axes, stoneAxes) : [];
    const matrix: Cell[] = candidates.map((c) => ({
      key: c.key,
      purityValueId: c.purityValueId,
      metalColourValueId: c.metalColourValueId,
      caratCode: c.caratCode,
      stoneOriginValueId: c.stoneOriginValueId,
      gemstoneValueId: c.gemstoneValueId,
      stoneShapeValueId: c.stoneShapeValueId,
      stoneCutValueId: c.stoneCutValueId,
      stoneColourValueId: c.stoneColourValueId,
      stoneClarityValueId: c.stoneClarityValueId,
      /* A candidate is a full nine-part identity now, so at most one variant
       * can match it — unlike the three-axis cell, which held four and hid
       * the rest. */
      variants: existingByKey.has(c.key) ? [existingByKey.get(c.key)!] : [],
    }));
    /*
     * Variants the axes no longer imply still have to be visible. CR-12: an
     * offered value switched off does not delete what already carries it,
     * and a row you cannot see is a row you cannot withdraw or reprice.
     */
    const shown = new Set(candidates.map((c) => c.key));
    for (const v of variants) {
      const k = variantKey(v);
      if (shown.has(k)) continue;
      matrix.push({
        key: k,
        purityValueId: v.purityValueId,
        metalColourValueId: v.metalColourValueId,
        caratCode: v.caratCode,
        stoneOriginValueId: v.stoneOriginValueId ?? null,
        gemstoneValueId: v.gemstoneValueId ?? null,
        stoneShapeValueId: v.stoneShapeValueId ?? null,
        stoneCutValueId: v.stoneCutValueId ?? null,
        stoneColourValueId: v.stoneColourValueId ?? null,
        stoneClarityValueId: v.stoneClarityValueId ?? null,
        variants: [v],
      });
    }
    // Counted in VARIANTS, not in cells: a cell can hold several.
    const live = matrix.reduce((n, m) => n + m.variants.length, 0);
    const missing = matrix.filter((m) => m.variants.length === 0).length;

    // One row per VARIANT, plus one per combination that does not exist yet.
    const rows: Row[] = [];
    for (const cell of matrix) {
      const { variants: vs, ...rest } = cell;
      if (vs.length === 0) rows.push({ ...rest, variant: undefined });
      else for (const v of vs) rows.push({ ...rest, key: v.id, variant: v });
    }

    // Grouped by purity and colour, collapsed — see the Panel below for why.
    const variantGroups = new Map<string, { purity: string | null; colour: string | null; rows: Row[] }>();
    for (const r of rows) {
      const key = `${r.purityValueId ?? '-'}/${r.metalColourValueId ?? '-'}`;
      if (!variantGroups.has(key)) {
        variantGroups.set(key, {
          purity: labelOf(allPurities, r.purityValueId),
          colour: labelOf(allColours, r.metalColourValueId),
          rows: [],
        });
      }
      variantGroups.get(key)!.rows.push(r);
    }

    /*
     * How many of those candidates the catalog does not hold yet — the
     * number a press of Generate would actually create. `candidates` above
     * IS that same array; this used to call `generate` a second time to get
     * it again.
     */
    const alreadyInCatalog = candidateTotal > GENERATION_CAP
      ? 0
      : candidates.reduce((n, c) => (existingByKey.has(c.key) ? n + 1 : n), 0);

    /** Each metal set the axes imply, with how many of its candidates exist —
     *  in the generator's own order (purity, then colour). */
    const metalSetCounts = new Map<string, {
      purityValueId: string | null; metalColourValueId: string | null; total: number; existing: number;
    }>();
    for (const c of candidates) {
      const k = metalSetKey(c);
      let entry = metalSetCounts.get(k);
      if (!entry) {
        entry = { purityValueId: c.purityValueId, metalColourValueId: c.metalColourValueId, total: 0, existing: 0 };
        metalSetCounts.set(k, entry);
      }
      entry.total += 1;
      if (existingByKey.has(c.key)) entry.existing += 1;
    }

    return { existingByKey, live, missing, rows, variantGroups, alreadyInCatalog, metalSetCounts };
  }, [variants, axes, stoneAxes, candidateTotal, allPurities, allColours]);

  const { existingByKey, live, missing, rows, variantGroups, alreadyInCatalog, metalSetCounts } = derived;
  const wouldCreate = candidateTotal - alreadyInCatalog;

  /*
   * The metal set Generate acts on: the one the admin chose, or — until they
   * choose, and again after a set is created — the first one still missing
   * variants, so working through a design is "create, next, create".
   */
  const metalSets = [...metalSetCounts.entries()].map(([key, m]) => ({
    key,
    label: `${labelOf(allPurities, m.purityValueId) ?? 'No purity'} · ${labelOf(allColours, m.metalColourValueId) ?? 'No colour'}`,
    total: m.total,
    existing: m.existing,
  }));
  const activeSet = metalSets.find((m) => m.key === chosenSet)
    ?? metalSets.find((m) => m.existing < m.total)
    ?? metalSets[0];
  const setWouldCreate = activeSet ? activeSet.total - activeSet.existing : 0;
  const setsComplete = metalSets.filter((m) => m.existing >= m.total).length;

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!isNew && !product) return null;

  const readOnly = !can('product.edit');
  const base = product ? draftOf(product) : EMPTY_DRAFT;
  const d: Draft = { ...base, ...edits };
  // Touching only the Offered-in or Stone panels changes neither `edits` nor
  // any Draft field, and Save has to be enabled for that alone — that gap is
  // exactly why "Save" used to look like it did nothing.
  //
  // The fourth clause covers everything that writes STRAIGHT to the server
  // with no local edit buffer at all — generating variances, the "Add a
  // variant" modal, a row's own Edit/Save once it has already landed. None
  // of those touch `edits`/`axesEdit`/`stoneAxesEdit`, so without this the
  // product could sit with a brand-new, unpublished piece underneath it
  // while the top action still read "Unpublish" — correct about nothing
  // having been taken down, silent about there being anything new to push
  // up. `unpublishIfLive`/`unpublishProductIfLive` (server-side, every
  // write path that can change what a retailer sees) already flips
  // `product.isPublished` to false the moment that happens; this just
  // reads the SAME signal instead of duplicating it as a separate flag —
  // true exactly when the product reads as a draft but still has real,
  // active content behind it, which is precisely "was live, now pending"
  // rather than "never published".
  const dirty = edits !== null || axesEdit !== null || stoneAxesEdit !== null
    || (!!product && !product.isPublished && variants.some((v) => v.isActive));

  const set = (k: keyof Draft, v: string) => setEdits({ ...edits, [k]: v });

  /**
   * What a NEW design still lacks before its variants can be created — the
   * fields `zCreateProduct` requires, mirrored here because `@miraki/contracts`
   * cannot be value-imported in the browser (see `api/hooks.ts`). The server
   * still validates; this only decides whether "Create N" is offered, so it is
   * enabled once the details are entered rather than only after a first Save.
   * Empty for a saved product: its details already passed.
   */
  const missingForCreate: string[] = !isNew ? [] : [
    ...(d.name.trim().length < 2 ? ['a product name'] : []),
    ...(!isCodePrefix(d.codePrefix)
      ? [d.codePrefix === ''
        ? 'a code prefix'
        : 'a code prefix of 2–24 capital letters, digits or hyphens (e.g. RING-01)']
      : []),
    ...(d.metalTypeCode === '' ? ['a metal type'] : []),
  ];
  const setAxis = (k: keyof Axes, v: string[]) => setAxesEdit({ ...axes, [k]: v });
  const setStoneAxis = (k: keyof StoneAxes, v: string[]) => setStoneAxesEdit({ ...stoneAxes, [k]: v });

  const purities = offered(group(groups, GROUP.purity), d.metalTypeCode);
  const colours = offered(group(groups, GROUP.metalColour), d.metalTypeCode);
  const carats = offered(group(groups, GROUP.carat), d.metalTypeCode);
  const metalTypes = group(groups, GROUP.metalType)?.values.filter((v) => v.isActive) ?? [];

  /** Stone has no metal-type dependency, so every active value is a candidate. */
  const allStoneOrigins = group(groups, GROUP.stoneOrigin)?.values.filter((v) => v.isActive) ?? [];
  const allGemstones = group(groups, GROUP.gemstone)?.values.filter((v) => v.isActive) ?? [];
  const allStoneShapes = group(groups, GROUP.stoneShape)?.values.filter((v) => v.isActive) ?? [];
  const allStoneCuts = group(groups, GROUP.stoneCut)?.values.filter((v) => v.isActive) ?? [];
  const allStoneColours = group(groups, GROUP.stoneColour)?.values.filter((v) => v.isActive) ?? [];
  const allStoneClarities = group(groups, GROUP.stoneClarity)?.values.filter((v) => v.isActive) ?? [];

  /** The option lists the proposal grid renders labels from. */
  const labels: AxisLabels = {
    purity: allPurities,
    colour: allColours,
    origin: allStoneOrigins,
    gemstone: allGemstones,
    shape: allStoneShapes,
    cut: allStoneCuts,
    stoneColour: allStoneColours,
    clarity: allStoneClarities,
  };

  /** `sent` is the body that went out — it lets a refusal tell "left blank" from "malformed". */
  async function run<T>(fn: () => Promise<T>, after?: (r: T) => void, sent?: unknown, fields: FieldMap = PRODUCT_FIELDS) {
    setFailed(null);
    try {
      // NOT `after?.(await fn())` — optional chaining short-circuits the
      // WHOLE expression when `after` is nullish, which means `await fn()`
      // never ran at all for every caller that omits `after` (Remove,
      // Restore, Make primary, …). Evaluating `fn()` on its own line makes
      // it unconditional; only the callback after it stays optional.
      const result = await fn();
      after?.(result);
    } catch (e) {
      setFailed(describeError(e, fields, sent));
    }
  }

  const body = () => {
    // Carat is stored on the variant by CODE, but `productOfferedValue` (like
    // every other axis) holds attribute-value ids — converted back here using
    // the same global list the Stone/Metal panels render their options from.
    const caratValues = group(groups, GROUP.carat)?.values ?? [];
    const caratIds = axes.caratCodes
      .map((code) => caratValues.find((c) => c.code === code)?.id)
      .filter((id): id is string => id !== undefined);
    return {
      name: d.name,
      description: nul(d.description),
      collectionId: nul(d.collectionId),
      categoryId: nul(d.categoryId),
      metalTypeCode: d.metalTypeCode,
      hsCode: nul(d.hsCode),
      countryOfOrigin: nul(d.countryOfOrigin),
      settingValueId: nul(d.settingValueId),
      finishValueId: nul(d.finishValueId),
      claspValueId: nul(d.claspValueId),
      backingValueId: nul(d.backingValueId),
      hallmarkValueId: nul(d.hallmarkValueId),
      // CR-45/CR-12 — what "Offered in" and "Stone" above actually persist.
      offeredValueIds: [
        ...axes.purityIds, ...axes.colourIds, ...caratIds,
        ...stoneAxes.stoneOriginIds, ...stoneAxes.gemstoneIds,
        ...stoneAxes.stoneShapeIds, ...stoneAxes.stoneCutIds,
        ...stoneAxes.stoneColourIds, ...stoneAxes.stoneClarityIds,
      ],
    };
  };

  // The Offered-in and Stone edit buffers are their own dirty flags — a save
  // triggered by changing only the Stone panel touches neither `edits` nor
  // any Draft field, and a button gated on `edits` alone would stay disabled
  // exactly when this screen most needs it enabled.
  const resetEdits = () => { setEdits(null); setAxesEdit(null); setStoneAxesEdit(null); };

  /**
   * The grid, as `zBulkImport` wants it.
   *
   * `products` is empty: this product exists, and only its variants are being
   * created. `productCode` is passed rather than read from `product` because a
   * NEW design's Create saves the product first and only then has a code the
   * variants can hang off — `product` has not refetched by then. Purity and metal colour go as CODES — the bulk route resolves them
   * in memory against the portal's active values. Stone rides in `attributes`,
   * because `zBulkVariant` has no stone column and opening a second bulk-create
   * path beside a route that already does 731 rows in seven seconds would be the
   * wrong trade.
   */
  /**
   * The proposal rows an import would send: made, not already a variant, and
   * priced. A row with no usable price is not sent. The contract makes the
   * field required for a reason: a variant with no price is one that could
   * reach a retailer priced at nothing.
   */
  const proposalRowsToSend = (): Candidate[] => (proposal?.rows ?? []).filter(
    (r) => !proposal!.notMade.has(r.key)
      && !existingByKey.has(r.key)
      && isPrice(proposal!.prices[r.key] ?? ''),
  );

  const importBody = (dryRun: boolean, productCode: string): BulkImportBody => {
    const variantRows: BulkVariantBody[] = [];
    if (proposal) {
      for (const r of proposalRowsToSend()) {
        const listPrice = proposal.prices[r.key]!;
        const sku = (proposal.skus[r.key] ?? '').trim();
        // Blank means "leave it unset", same as a blank SKU means "derive
        // it" — each is sent only when the author actually typed something,
        // common default or per-row override alike; the route already
        // accepts all three (`zBulkVariant`), the grid just never sent them.
        const netWeight = (proposal.netWeights[r.key] ?? '').trim();
        const grossWeight = (proposal.grossWeights[r.key] ?? '').trim();
        const totalCarat = (proposal.totalCarats[r.key] ?? '').trim();
        variantRows.push({
          productCode,
          // Supplied wins over derived; blank means the server derives it. The
          // browser never builds a code either way.
          ...(sku === '' ? {} : { code: sku }),
          purity: codeOf(allPurities, r.purityValueId),
          metalColour: codeOf(allColours, r.metalColourValueId),
          caratCode: r.caratCode,
          listPrice,
          ...(netWeight === '' ? {} : { netMetalWeightG: netWeight }),
          ...(grossWeight === '' ? {} : { grossWeightG: grossWeight }),
          ...(totalCarat === '' ? {} : { totalCaratWeight: totalCarat }),
          ...stoneCodes(r, labels),
          attributes: {},
        });
      }
    }
    return { variants: variantRows, dryRun };
  };

  /**
   * Generation is a PROPOSAL. It writes nothing, which is the whole point:
   * "show me what this would be" and "create 252 products" must not be the
   * same button.
   */
  /**
   * Rows the author has priced but not yet created.
   *
   * A price alone is enough: every other field of a candidate is already fixed
   * by its position in the matrix, which is the whole point of the matrix.
   */
  const pricedDrafts = rows.filter(
    (r) => !r.variant && (draftPrices[r.key] ?? '').trim() !== '',
  );

  /** A drafted row's weights for the create body — a blank box is left out, not sent as "". */
  const draftWeightBody = (key: string): Partial<Record<WeightField, string>> => {
    const out: Partial<Record<WeightField, string>> = {};
    for (const f of WEIGHT_FIELDS) {
      const value = (draftWeights[key]?.[f] ?? '').trim();
      if (value !== '') out[f] = value;
    }
    return out;
  };

  /**
   * Created one at a time, in order, and reported honestly if one fails.
   *
   * `createVariant` is the single-variant route; the bulk path is the import
   * below, which takes a whole workbook. For the handful of rows a person
   * prices by hand this is the right size, and doing them in order means a
   * refusal names the row that caused it instead of failing the batch.
   */
  const createPriced = () => {
    if (!product || pricedDrafts.length === 0) return;
    setCreatePricedPending(true);
    setFailed(null);
    void (async () => {
      const done: string[] = [];
      try {
        for (const r of pricedDrafts) {
          await createVariant.mutateAsync({
            productId: product.id,
            body: {
              productId: product.id,
              purityValueId: r.purityValueId,
              metalColourValueId: r.metalColourValueId,
              caratCode: r.caratCode,
              stoneOriginValueId: r.stoneOriginValueId,
              gemstoneValueId: r.gemstoneValueId,
              stoneShapeValueId: r.stoneShapeValueId,
              stoneCutValueId: r.stoneCutValueId,
              stoneColourValueId: r.stoneColourValueId,
              stoneClarityValueId: r.stoneClarityValueId,
              ...draftWeightBody(r.key),
              listPrice: draftPrices[r.key]!.trim(),
              attributes: {},
            },
          });
          done.push(r.key);
        }
      } catch (e) {
        setFailed(
          `${done.length} of ${pricedDrafts.length} created. `
          + describeError(e, PRODUCT_FIELDS),
        );
      } finally {
        // Clear only what actually landed, so a partial run leaves the rest
        // still typed in and retryable rather than silently lost.
        if (done.length > 0) {
          setDraftPrices((prev) => {
            const next = { ...prev };
            for (const k of done) delete next[k];
            return next;
          });
          setDraftWeights((prev) => {
            const next = { ...prev };
            for (const k of done) delete next[k];
            return next;
          });
        }
        setCreatePricedPending(false);
      }
    })();
  };

  /**
   * Withdraw, redefined: a genuine removal, not `isActive: false` — ONE
   * request for the whole selection, not one per variant. This used to loop
   * the single-variant route: correct, but a group (every variant sharing a
   * purity/colour, which can be dozens) meant that many sequential HTTP
   * round trips, and the UI had nothing to show until every one of them
   * finished — the "did this actually do anything" a slow group withdrawal
   * produced. Selecting a whole group (its own checkbox, above) and pressing
   * this same button is how the "remove the whole group" case reaches here:
   * it is not a separate action, just every variant in that group being
   * selected and sent together.
   */
  const bulkWithdraw = () => {
    if (!product || selectedVariants.size === 0) return;
    setBulkWithdrawPending(true);
    setFailed(null);
    const ids = [...selectedVariants];
    void deleteVariants.mutateAsync({ productId: product.id, ids }).then(
      (result) => {
        const refusedIds = new Set(result.refused.map((r) => r.id));
        const removed = ids.filter((id) => !refusedIds.has(id));
        setFailed(
          result.refused.length === 0 ? null : (
            `${removed.length} of ${ids.length} withdrawn. `
            + result.refused.slice(0, 3).map((r) => r.reason).join('; ')
            + (result.refused.length > 3 ? ` and ${result.refused.length - 3} more refused.` : '.')
          ),
        );
        // Only what actually landed drops out of the selection — a partial
        // run leaves the rest still checked, ready to retry.
        if (removed.length > 0) {
          setSelectedVariants((prev) => {
            const next = new Set(prev);
            for (const id of removed) next.delete(id);
            return next;
          });
        }
      },
      (e: unknown) => setFailed(e instanceof ApiError ? e.message : 'That did not work'),
    ).finally(() => setBulkWithdrawPending(false));
  };

  /** One metal set's candidates only — the other sets are not touched. */
  const startProposal = () => {
    if (!activeSet) return;
    setImportResult(null);
    setProposal({
      rows: generate(axes, stoneAxes).filter((c) => metalSetKey(c) === activeSet.key),
      metalSetLabel: activeSet.label,
      prices: {}, skus: {}, netWeights: {}, grossWeights: {}, totalCarats: {},
      notMade: new Set(),
    });
  };

  /** The proposal grid's Create — always a real commit, never a dry run. */
  const runImport = () => {
    // A new design has no id for a variant to belong to yet — its Create is
    // Save, which writes the product and then this same import (`save`).
    if (isNew) { save(true); return; }
    if (!product) return;
    setImportResult(null);
    void run(
      () => bulkImport.mutateAsync(importBody(false, product.codePrefix)),
      (r) => {
        setImportResult(r);
        // Only a clean commit clears the grid; one with refusals still has
        // work in it.
        if (r.refused.length === 0) { setProposal(null); setChosenSet(null); }
      },
      importBody(false, product.codePrefix),
      PROPOSAL_FIELDS,
    );
  };

  /**
   * `withProposal` — a new design's "Create N" in the proposal grid. The grid
   * is offered as soon as the required details are in (`newProductReady`), not
   * only after a first Save, so its Create has to save the product itself
   * before the variants have an id to belong to.
   */
  const save = (withProposal = false) => {
    if (isNew) {
      /*
       * Saving a NEW design also writes the rows that were priced while it did
       * not exist yet.
       *
       * A variant belongs to a product id, so the prices typed above could not
       * be written when they were entered. They are held by candidate key and
       * flushed here, in one go, before navigating — otherwise an author prices
       * eighty-one rows, presses Save, and arrives at a product with none of
       * them, having been given no hint that the work was discarded.
       *
       * A failure to create the variants does NOT undo the product: it exists,
       * it is correct, and the rows can be created from its own screen. What
       * matters is saying so rather than landing somewhere that looks finished.
       */
      const createBody = { ...body(), codePrefix: d.codePrefix };
      void run(
        () => createProduct.mutateAsync(createBody),
        (created) => {
          resetEdits();
          // The proposal grid and the inline price boxes offer the same
          // candidates. A row priced in both is created once, by the import —
          // otherwise both paths would create it and the second would fail on
          // the code they both derive, after the product was already saved.
          const sentByImport = new Set(withProposal ? proposalRowsToSend().map((r) => r.key) : []);
          const toCreate = pricedDrafts.filter((r) => !sentByImport.has(r.key));
          if (toCreate.length === 0 && !withProposal) {
            void navigate(`/catalog/products/${created.codePrefix}`, { replace: true });
            return;
          }
          setCreatePricedPending(true);
          void (async () => {
            let done = 0;
            try {
              if (withProposal) {
                /*
                 * The same route and body the saved-product Create sends. The
                 * screen survives the navigate below (same route, new param),
                 * so the result renders exactly as it does there — and a clean
                 * commit clears the grid, while refusals leave it to retry.
                 */
                setImportResult(null);
                try {
                  const r = await bulkImport.mutateAsync(importBody(false, created.codePrefix));
                  setImportResult(r);
                  if (r.refused.length === 0) { setProposal(null); setChosenSet(null); }
                } catch (e) {
                  setFailed(
                    'The design was saved, but its variants were not created — '
                    + (e instanceof ApiError ? e.message : 'the request was refused')
                    + '. Create them from this screen.',
                  );
                }
              }
              for (const r of toCreate) {
                await createVariant.mutateAsync({
                  productId: created.id,
                  body: {
                    productId: created.id,
                    purityValueId: r.purityValueId,
                    metalColourValueId: r.metalColourValueId,
                    caratCode: r.caratCode,
                    stoneOriginValueId: r.stoneOriginValueId,
                    gemstoneValueId: r.gemstoneValueId,
                    stoneShapeValueId: r.stoneShapeValueId,
                    stoneCutValueId: r.stoneCutValueId,
                    stoneColourValueId: r.stoneColourValueId,
                    stoneClarityValueId: r.stoneClarityValueId,
                    ...draftWeightBody(r.key),
                    listPrice: draftPrices[r.key]!.trim(),
                    attributes: {},
                  },
                });
                done += 1;
              }
              setDraftPrices({});
              setDraftWeights({});
            } catch (e) {
              setFailed(
                `The design was saved. ${done} of ${toCreate.length} priced variants were created — `
                + describeError(e, PRODUCT_FIELDS, undefined, 'the rest were refused')
                + ' Create the remainder from this screen.',
              );
            } finally {
              setCreatePricedPending(false);
              void navigate(`/catalog/products/${created.codePrefix}`, { replace: true });
            }
          })();
        },
        createBody,
      );
    } else if (product) {
      // A variance edit sitting in the row's own draft is "unsaved" too, so
      // the top Save button flushes it the same way the row's own Save does
      // — `saveFields`, not a second write path — chained ahead of any
      // product-level save rather than fired alongside it, so one write's
      // failure can't race the other's and overwrite its message.
      if (editingVariant && rowSavable(editingVariant)) {
        saveFields(editingVariant, () => {
          if (dirty) void run(() => updateProduct.mutateAsync({ id: product.id, body: body() }), resetEdits, body());
        });
      } else if (dirty) {
        void run(() => updateProduct.mutateAsync({ id: product.id, body: body() }), resetEdits, body());
      }
    }
  };

  /*
   * The code, and the one number that is not visible anywhere else.
   *
   * It read `ER-421970 · No collection · No category · Gold · 6 variants from 2 purities × 3
   * colours × 1 carat`, wrapped to two lines, and every part of it except those two was an
   * EDITABLE FIELD in a panel a few pixels below: Collection and Category in Identity, Metal
   * type in Offered in, and the "2 purities × 3 colours × 1 carat" is a reading of the axes
   * that panel is for setting. A header that restates the form under it costs height to say
   * what the reader is about to see anyway, and it says it worse — "No collection" where the
   * field below offers the choice.
   *
   * The variant count stays because it is DERIVED: a person cannot get it by looking at the
   * axes, only by multiplying them. Read from `matrixSize`, never multiplied here — this used
   * to count the metal frame alone, which is why the header once said nine while the panel
   * below it said a hundred and eighty.
   */
  const subtitle = [
    d.codePrefix || 'No prefix yet',
    plural(size.total, 'variant', 'variants'),
  ].join(' · ');

  /** The trimmed display form of one stored weight — `null` stays `null` (Weight already draws that as a dash). */
  const weightStr = (v: VariantRow, field: WeightField): string =>
    (v[field] === null ? '' : trimAmount(v[field]));

  /** The trimmed display form of any of the four editable fields, price included. */
  const fieldStr = (v: VariantRow, field: EditableField): string => {
    if (field === 'listPrice') return trimAmount(v.listPrice);
    /* RP is money like the price, not a weight — and unlike the price it can be
       null, on a variant written before migration 0032 backfilled it. */
    if (field === 'rpPrice') return v.rpPrice === null ? '' : trimAmount(v.rpPrice);
    return weightStr(v, field);
  };

  const startEdit = (v: VariantRow) => {
    setEditingRow(v.id);
    setFieldDrafts({
      listPrice: fieldStr(v, 'listPrice'),
      rpPrice: fieldStr(v, 'rpPrice'),
      netMetalWeightG: fieldStr(v, 'netMetalWeightG'),
      grossWeightG: fieldStr(v, 'grossWeightG'),
      totalCaratWeight: fieldStr(v, 'totalCaratWeight'),
    });
  };

  /**
   * Save stays disabled until at least one of the four actually differs from
   * what is stored, and never on a blank price — required, never nullable
   * (zMoney), so an emptied box has nothing safe to send. Shared by the
   * row's own Save button and the top one (near Publish/Unpublish), which
   * gate on exactly the same question.
   */
  const rowSavable = (v: VariantRow): boolean =>
    fieldDrafts.listPrice.trim() !== '' && (
      fieldDrafts.listPrice.trim() !== fieldStr(v, 'listPrice')
      /* A blank RP box means "leave it alone", so it never blocks saving
         something else: `zMoney.optional()` has no null to send, and after
         0032's backfill the figure is always populated anyway. */
      || (fieldDrafts.rpPrice.trim() !== '' && fieldDrafts.rpPrice.trim() !== fieldStr(v, 'rpPrice'))
      || WEIGHT_FIELDS.some((f) => fieldDrafts[f].trim() !== fieldStr(v, f))
    );

  /**
   * Only the fields that actually changed are sent — one request, not up to
   * four. `after` runs once the patch lands, chained rather than fired
   * alongside anything else — the top Save button uses it to flush a
   * product-level save only once the variance write succeeds, so the two
   * requests' failure messages can never race each other.
   */
  const saveFields = (v: VariantRow, after?: () => void) => {
    const patch: Record<string, string | null> = {};
    const priceNext = fieldDrafts.listPrice.trim();
    if (priceNext !== '' && priceNext !== fieldStr(v, 'listPrice')) patch.listPrice = priceNext;
    const rpNext = fieldDrafts.rpPrice.trim();
    if (rpNext !== '' && rpNext !== fieldStr(v, 'rpPrice')) patch.rpPrice = rpNext;
    for (const f of WEIGHT_FIELDS) {
      const next = fieldDrafts[f].trim();
      if (next !== fieldStr(v, f)) patch[f] = next === '' ? null : next;
    }
    if (Object.keys(patch).length === 0) { setEditingRow(null); after?.(); return; }
    setRowSaving(true);
    void updateVariant.mutateAsync({ productId: product!.id, variantId: v.id, body: patch }).then(
      () => { setFailed(null); setEditingRow(null); after?.(); },
      (err: unknown) => setFailed(describeError(err, PRODUCT_FIELDS, patch)),
    ).finally(() => setRowSaving(false));
  };

  /**
   * The row mid-edit, if any — so the TOP Save button (near Publish/Unpublish)
   * can see a variance edit too, not just the row's own Save. A published
   * product's variant patch already unpublishes it server-side
   * (`CatalogService.unpublishIfLive`), so flushing it from here needs no new
   * backend behaviour: it is the exact same `saveFields` the row's own button
   * calls, just reachable from the top as well.
   */
  const editingVariant = editingRow ? (variants.find((v) => v.id === editingRow) ?? null) : null;

  /**
   * Net weight, gross weight and total carat — each its own column, all
   * three driven by the ONE Edit/Save pair in the row's action column
   * (`editingRow`), the same trigger List price's own column now shares.
   * Reads and writes are still per-field strings, never parsed (NF-04) —
   * this only shares the trigger, not the values.
   *
   * The unit lives in the column header (`Net weight (g)` etc.), not on
   * every value — eighty-one rows each repeating "g" or "ct" said nothing
   * eighty-one times that the header did not already say once.
   */
  const weightCell = (m: Row, field: WeightField) => {
    const v = m.variant;
    if (!v && !readOnly) {
      // Not created yet: typed beside the draft price and sent with it on
      // creation (`draftWeightBody`), so the row need not be reopened to finish it.
      return (
        <input
          className="mk-price-in"
          inputMode="decimal"
          value={draftWeights[m.key]?.[field] ?? ''}
          aria-label={`${WEIGHT_LABEL[field]} for the proposed variant ${
            [labelOf(allPurities, m.purityValueId), labelOf(allColours, m.metalColourValueId)]
              .filter(Boolean).join(' ')}`}
          onChange={(e) => setDraftWeights((prev) => ({
            ...prev, [m.key]: { ...prev[m.key], [field]: e.target.value },
          }))}
        />
      );
    }
    const display = v ? weightStr(v, field) : '';
    if (!v || readOnly || editingRow !== v.id) {
      return display === '' ? <span className="mk-dim">—</span> : <span className="mk-money">{display}</span>;
    }
    return (
      <input
        className="mk-price-in"
        inputMode="decimal"
        value={fieldDrafts[field]}
        disabled={rowSaving}
        aria-label={`${WEIGHT_LABEL[field]} for ${v.code}`}
        onChange={(e) => setFieldDrafts((prev) => ({ ...prev, [field]: e.target.value }))}
      />
    );
  };

  const matrixColumns: Column<Row>[] = [
    // Bulk-withdraw selection — a variant only; a "not created" row has
    // nothing to withdraw, so its cell is blank rather than a dead checkbox.
    ...(readOnly ? [] : [{
      header: '',
      width: '3%',
      center: true,
      render: (m: Row) => {
        const v = m.variant;
        if (!v) return null;
        return (
          <input
            type="checkbox"
            checked={selectedVariants.has(v.id)}
            aria-label={`Select ${v.code}`}
            onChange={() => setSelectedVariants((prev) => {
              const next = new Set(prev);
              if (next.has(v.id)) next.delete(v.id); else next.add(v.id);
              return next;
            })}
          />
        );
      },
    } as Column<Row>]),
    {
      header: 'Variant code',
      // A derived code is short, but a SUPPLIED one (the client's own SKUs,
      // up to 64 chars) is not — widest of the fixed columns for that
      // reason, though `code` still wraps rather than forcing the column
      // wider than this once `table-layout: fixed` is what decides it.
      // Derived by the server. Rendered, never an input — see the file header.
      width: '14%',
      render: (m) => (m.variant ? <code>{m.variant.code}</code> : <span className="mk-dim">Derived on creation</span>),
    },
    { header: 'Purity', width: '7%', center: true, render: (m) => labelOf(allPurities, m.purityValueId) ?? '—' },
    { header: 'Colour', width: '7%', center: true, render: (m) => labelOf(allColours, m.metalColourValueId) ?? '—' },
    {
      header: 'Carat',
      width: '6%',
      center: true,
      render: (m) => (m.caratCode ? <code>{m.caratCode}</code> : <span className="mk-dim">—</span>),
    },
    {
      header: 'Stone',
      width: '15%',
      /*
       * Origin · Material · Shape · Cut · Colour · Clarity, only the parts
       * actually set — a plain metal variant carries none and shows a dash.
       *
       * Reads the CELL, not just a created variant. This rendered only
       * `m.variant`, so every uncreated row showed a dash whatever stone it
       * was proposing — which is what a hundred and eighty identical-looking
       * "not created" rows were hiding.
       */
      render: (m) => {
        const v = m.variant;
        const parts = v
          ? [v.stoneOrigin, v.gemstone, v.stoneShape, v.stoneCut, v.stoneColour, v.stoneClarity]
          : [
            labelOf(allStoneOrigins, m.stoneOriginValueId),
            labelOf(allGemstones, m.gemstoneValueId),
            labelOf(allStoneShapes, m.stoneShapeValueId),
            labelOf(allStoneCuts, m.stoneCutValueId),
            labelOf(allStoneColours, m.stoneColourValueId),
            labelOf(allStoneClarities, m.stoneClarityValueId),
          ];
        const set = parts.filter(Boolean);
        return set.length > 0 ? <span>{set.join(' · ')}</span> : <span className="mk-dim">—</span>;
      },
    },
    {
      // The header itself is `text-transform: uppercase` (every column's
      // is), which reads a plain "(g)" as "(G)" — `.mk-unit` counteracts it
      // for just the unit, so the label shouts and the unit does not.
      // Centered, not right-aligned — the same reason the Product list's
      // Price column moved off `numeric`: a right-aligned figure reads as
      // pinned to the far edge of a column wider than the value itself,
      // where a centered one sits under its own (also centered) header.
      header: <>Net weight <span className="mk-unit">(g)</span></>,
      center: true,
      width: '11%',
      render: (m) => weightCell(m, 'netMetalWeightG'),
    },
    {
      header: <>Gross weight <span className="mk-unit">(g)</span></>,
      center: true,
      width: '11%',
      render: (m) => weightCell(m, 'grossWeightG'),
    },
    {
      // Kept as "Total carat", not "Carat" — the matrix already has a
      // "Carat" column (the code-driving bucket, e.g. "030") a few columns
      // to the left; renaming this one to match would make two differently-
      // meaning columns read as the same thing.
      header: <>Total carat <span className="mk-unit">(ct)</span></>,
      center: true,
      width: '10%',
      render: (m) => weightCell(m, 'totalCaratWeight'),
    },
    {
      /*
       * Editable in place, on the row — the same ONE Edit/Save pair the three
       * weight columns share (`editingRow`), not a save-on-blur of its own
       * any more. List price used to be the one field editable ahead of the
       * other three, which is exactly what made them read as gated behind
       * it; now all four open and close together.
       *
       * Held as a STRING from the input to the API (NF-04). Nothing here
       * parses it; the server is the only thing that validates it, and an
       * unparseable value is refused there rather than silently rounded
       * here.
       */
      header: 'List price',
      center: true,
      width: '10%',
      render: (m) => {
        if (readOnly) {
          return m.variant
            ? <Money amount={m.variant.listPrice} currency={product?.currency} />
            : <span className="mk-dim">—</span>;
        }
        const v = m.variant;
        if (!v) {
          // Not created yet: staging a price here is what proposes the row
          // for one-off creation (`pricedDrafts`/`createPriced`) — a
          // separate mechanism from the Edit/Save pair below, untouched by
          // this change.
          return (
            <span className="mk-price-cell">
              <input
                className="mk-price-in"
                inputMode="decimal"
                placeholder="0.00"
                value={draftPrices[m.key] ?? ''}
                aria-label={`List price for the proposed variant ${
                  [labelOf(allPurities, m.purityValueId), labelOf(allColours, m.metalColourValueId)]
                    .filter(Boolean).join(' ')}`}
                onChange={(e) => setDraftPrices((prev) => ({ ...prev, [m.key]: e.target.value }))}
              />
              {product?.currency ? <span className="mk-dim">{product.currency}</span> : null}
            </span>
          );
        }
        if (editingRow !== v.id) return <Money amount={v.listPrice} currency={product?.currency} />;
        return (
          <span className="mk-price-cell">
            <input
              className="mk-price-in"
              inputMode="decimal"
              value={fieldDrafts.listPrice}
              disabled={rowSaving}
              aria-label={`List price for ${v.code}`}
              onChange={(e) => setFieldDrafts((prev) => ({ ...prev, listPrice: e.target.value }))}
            />
            {product?.currency ? <span className="mk-dim">{product.currency}</span> : null}
          </span>
        );
      },
    },
    {
      /**
       * RP — PR-02, the retailer's suggested selling price, shown beside the
       * wholesale price because the requirement is that BOTH are visible and a
       * single "price" column cannot carry two figures.
       *
       * Editable here and nowhere else: PR-05 defaults it to 3x wholesale on
       * write, and D59 lets Admin override it per variant, so this box is the
       * only place that override can be made. Leave it blank and the stored
       * figure is left alone — `zMoney.optional()` has no null to send, and
       * nothing should silently reset a figure somebody chose.
       *
       * A variant written before migration 0032 shows a dash rather than a
       * guess. PR-03 — the retailer is invoiced on the wholesale price, never
       * on this.
       */
      header: 'RP',
      center: true,
      width: '10%',
      render: (m) => {
        const v = m.variant;
        if (!v) return <span className="mk-dim">—</span>;
        const shown = v.rpPrice === null
          ? <span className="mk-dim">—</span>
          : <Money amount={v.rpPrice} currency={product?.currency} />;
        if (readOnly || editingRow !== v.id) return shown;
        return (
          <span className="mk-price-cell">
            <input
              className="mk-price-in"
              inputMode="decimal"
              value={fieldDrafts.rpPrice}
              disabled={rowSaving}
              placeholder="3x wholesale"
              aria-label={`RP for ${v.code}`}
              onChange={(e) => setFieldDrafts((prev) => ({ ...prev, rpPrice: e.target.value }))}
            />
            {product?.currency ? <span className="mk-dim">{product.currency}</span> : null}
          </span>
        );
      },
    },
    {
      header: '',
      center: true,
      width: '6%',
      render: (m) => {
        if (!m.variant || readOnly) return null;
        const v = m.variant;
        const isEditingRow = editingRow === v.id;
        return (
          <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
            {/* One Edit covers list price, net weight, gross weight and
                total carat at once — none of the four gates any other any
                more. Save stays disabled until one of the four actually
                changes (or, for list price alone, is left blank — the
                field is required and an empty box is never sent). Withdraw
                now deletes outright (the bulk action beside "Add a
                variant") rather than deactivating, so there is no more
                withdrawn-but-recoverable state for a per-row Restore to
                reach — removing a row here is undoable only by adding it
                back. */}
            {isEditingRow ? (
              <Button
                size="sm"
                disabled={rowSaving || !rowSavable(v)}
                onClick={() => saveFields(v)}
              >
                Save
              </Button>
            ) : (
              <Button size="sm" tone="ghost" onClick={() => startEdit(v)}>Edit</Button>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <>
      {/* A detail screen reached from a list needs a way back to it. Browser back
          works, but a person who arrived by pasting a code has no history. */}
      <div className="mk-backlink">
        <Link to="/catalog/products">&larr; All products</Link>
      </div>
      <PageHeader
        title={isNew ? 'New product' : d.name}
        sub={subtitle}
        actions={
          /*
           * Draft sits OUTSIDE the read-only branch on purpose.
           *
           * It is the product's state, not a thing you can do to it, so somebody who can only
           * look still has to see it — a read-only viewer of a draft who is told "Read only" and
           * nothing else would reasonably assume retailers can see what they are looking at. The
           * banner this replaced showed regardless of permission, and so does this.
           *
           * Same word and same tone the LIST uses for it, so the state a person saw in the Status
           * column is the state they see here. The sentence the banner carried is on the chip,
           * for whoever meets a draft for the first time.
           */
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--mk-space-2)' }}>
            {product && !product.isPublished ? (
              <Chip
                tone="lapse"
                title="No retailer sees this product, and no order can name a variant of it, until it is published."
              >
                Draft
              </Chip>
            ) : null}
            {readOnly ? <Chip tone="run">Read only</Chip> : (
              <>
              {product ? (
                <Button
                  size="sm"
                  tone="ghost"
                  disabled={setPublished.isPending}
                  // Publishing is a separate action from saving, and it is checked
                  // again server-side at the moment of publishing: a value
                  // deactivated after the draft was written has to stop it going
                  // live, which a save-time check alone would miss.
                  onClick={() => {
                    const nextPublished = !product.isPublished;
                    setBanner(null);
                    void run(
                      () => setPublished.mutateAsync({ id: product.id, isPublished: nextPublished }),
                      (r) => {
                        const variants = `${r.variantsChanged} variant${r.variantsChanged === 1 ? '' : 's'}`;
                        // A publish is a submission: the author is done with this
                        // product, so they go back to the list and are told there.
                        // A withdraw stays — it is usually the first step of an edit.
                        // A refusal throws, so it never reaches here and stays on
                        // this screen, beside what needs fixing.
                        if (nextPublished) {
                          void navigate('/catalog/products', {
                            state: { banner: `${product.name} (${product.codePrefix}) published — ${variants} with it.` },
                          });
                        } else {
                          setBanner(`Withdrawn — ${variants} with it.`);
                        }
                      },
                    );
                  }}
                >
                  {product.isPublished ? 'Unpublish' : 'Publish'}
                </Button>
              ) : null}
              <Button
                size="sm"
                // A pending row edit (any of list price / net / gross / total
                // carat — the Variance block's one Edit/Save) counts as
                // unsaved too, so this button is not blind to it.
                disabled={
                  (!dirty && !(editingVariant && rowSavable(editingVariant)))
                  || createProduct.isPending || updateProduct.isPending || rowSaving
                }
                onClick={() => save()}
              >
                Save
              </Button>
              </>
            )}
          </span>
        }
      />

      {failed ? <Alert tone="stop"><span className="mk-lines">{failed}</span></Alert> : null}
      {banner ? <Alert tone="them">{banner}</Alert> : null}
      {/*
        * The draft state is a CHIP beside the title now, not a full-width alert.
        *
        * It mattered — a draft is invisible to every retailer — but the screen already says so
        * twice over: the action button reads Publish rather than Save, and a product that could
        * not be published would not offer it. A banner restating that cost a block of height on
        * every visit to every unpublished product, and the people who see this screen see it
        * daily.
        *
        * The consequence is kept on the chip's tooltip rather than deleted: someone meeting a
        * draft for the first time can still find out what it means, and nobody pays height for
        * a sentence they have read.
        */}

      <div className="mk-grid2">
        <Panel title="Identity" pad>
          <Field label="Product name">
            <input value={d.name} disabled={readOnly} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <div className="mk-fset">
            <Field label="Collection">
              <select value={d.collectionId} disabled={readOnly} onChange={(e) => set('collectionId', e.target.value)}>
                <option value="">Not in a collection</option>
                {(collections ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <select value={d.categoryId} disabled={readOnly} onChange={(e) => set('categoryId', e.target.value)}>
                <option value="">Uncategorised</option>
                {(categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          {isNew ? (
            <Field
              label="Code prefix"
              hint="Uppercase and hyphens. Every variant code is built from this — prefix, purity, colour, carat — so it cannot be changed once a variant exists."
            >
              <input
                value={d.codePrefix}
                disabled={readOnly}
                onChange={(e) => set('codePrefix', e.target.value.toUpperCase())}
              />
            </Field>
          ) : (
            <Field
              label="Code prefix"
              hint="Fixed. Changing it would orphan every variant code built from it, which is why the contract has no field for it on an update."
            >
              <div><code>{d.codePrefix}</code></div>
            </Field>
          )}
          <Field label="Description">
            <textarea
              rows={4}
              value={d.description}
              disabled={readOnly}
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>
        </Panel>

        {/* CR-45 — metal type comes first, because purity and colour depend on it. */}
        <Panel title="Offered in" pad>
          <div className="mk-subhead">Metal</div>
          <div className="mk-fset">
            <Field label="Metal type">
              <select
                value={d.metalTypeCode}
                disabled={readOnly}
                // Switching the metal resets both dependents: 18k is not a platinum
                // purity, and platinum has no colour choice at all.
                onChange={(e) => {
                  set('metalTypeCode', e.target.value);
                  setAxesEdit({ ...axes, purityIds: [], colourIds: [] });
                }}
              >
                <option value="">Choose a metal</option>
                {metalTypes.map((m) => <option key={m.id} value={m.code}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Centre stone carat">
              <MultiSelect
                label="Centre stone carat"
                options={carats.map((c) => ({ v: c.code, label: c.label }))}
                value={axes.caratCodes}
                disabled={readOnly}
                onChange={(next) => setAxis('caratCodes', next)}
              />
            </Field>
          </div>
          <div className="mk-fset">
            <Field label="Purity">
              <MultiSelect
                label="Purity"
                options={purities.map((p) => ({ v: p.id, label: p.label }))}
                value={axes.purityIds}
                disabled={readOnly || !d.metalTypeCode}
                onChange={(next) => setAxis('purityIds', next)}
              />
            </Field>
            <Field label="Metal colour">
              {colours.length ? (
                <MultiSelect
                  label="Metal colour"
                  options={colours.map((c) => ({ v: c.id, label: c.label }))}
                  value={axes.colourIds}
                  disabled={readOnly}
                  onChange={(next) => setAxis('colourIds', next)}
                />
              ) : (
                <p className="mk-hint">
                  This metal has no colour choice, so the design has one set of variants
                  and no colour axis.
                </p>
              )}
            </Field>
          </div>

          {/* Stone, folded into the same panel as the metal axes rather than its
              own block — Offered In is where the rest of the variant frame
              already lives, and has the room. Two jobs, unchanged by the move:
              the selection is what the retailer's own Stone dropdowns are
              built from (`shop.controller.ts`'s `stoneOptions`), AND it is the
              second half of the variant matrix — Origin × Material × Shape ×
              Cut × Colour × Clarity, crossed with the metal frame above by the
              generator below. */}
          <div className="mk-subhead">Stone</div>
          <div className="mk-fset">
            <Field label="Origin" >
              <MultiSelect
                label="Origin"
                options={allStoneOrigins.map((v) => ({ v: v.id, label: v.label }))}
                value={stoneAxes.stoneOriginIds}
                disabled={readOnly}
                onChange={(next) => setStoneAxis('stoneOriginIds', next)}
              />
            </Field>
            <Field label="Stone Type">
              <MultiSelect
                label="Stone Type"
                options={allGemstones.map((v) => ({ v: v.id, label: v.label }))}
                value={stoneAxes.gemstoneIds}
                disabled={readOnly}
                onChange={(next) => setStoneAxis('gemstoneIds', next)}
              />
            </Field>
            <Field label="Shape">
              <MultiSelect
                label="Shape"
                options={allStoneShapes.map((v) => ({ v: v.id, label: v.label }))}
                value={stoneAxes.stoneShapeIds}
                disabled={readOnly}
                onChange={(next) => setStoneAxis('stoneShapeIds', next)}
              />
            </Field>
            <Field label="Cut">
              <MultiSelect
                label="Cut"
                options={allStoneCuts.map((v) => ({ v: v.id, label: v.label }))}
                value={stoneAxes.stoneCutIds}
                disabled={readOnly}
                onChange={(next) => setStoneAxis('stoneCutIds', next)}
              />
            </Field>
          </div>
          <div className="mk-fset">
            <Field label="Colour">
              <MultiSelect
                label="Colour"
                options={allStoneColours.map((v) => ({ v: v.id, label: v.label }))}
                value={stoneAxes.stoneColourIds}
                disabled={readOnly}
                onChange={(next) => setStoneAxis('stoneColourIds', next)}
              />
            </Field>
            <Field label="Clarity">
              <MultiSelect
                label="Clarity"
                options={allStoneClarities.map((v) => ({ v: v.id, label: v.label }))}
                value={stoneAxes.stoneClarityIds}
                disabled={readOnly}
                onChange={(next) => setStoneAxis('stoneClarityIds', next)}
              />
            </Field>
          </div>
        </Panel>
      </div>

      {product ? (
        <CertificationsPanel
          certifications={product.certificateRules ?? []}
          attachments={certAttachments.data}
          variantCodes={variants.map((v) => v.code)}
          readOnly={readOnly}
          pending={saveRules.isPending}
          onUpload={(certificateId, file) =>
            addCertAttachment.mutateAsync({ productId: product.id, certificateId, file })}
          onRemoveAttachment={(a) =>
            deleteCertAttachment.mutateAsync({ productId: product.id, attachmentId: a.id })}
          onOpenAttachment={openCertificateAttachment}
          // Not `run()` here: `run()` catches its own failures and never
          // rethrows, so the promise it returns always resolves — the panel
          // would clear its draft on a REFUSED save exactly as it would on a
          // successful one. This needs the two to read differently, so the
          // rejection is surfaced (`failed`, same message `run()` would have
          // shown) and then passed on, unswallowed.
          onSave={(body) => {
            setFailed(null);
            return saveRules.mutateAsync({ productId: product.id, rules: body }).catch((e: unknown) => {
              setFailed(e instanceof ApiError ? e.message : 'That did not work');
              throw e;
            });
          }}
        />
      ) : null}

      <div className="mk-grid2">
        {/* CR-47 — a customs declaration needs a tariff code and an origin. */}
        <Panel title="Customs & origin" pad>
          <div className="mk-fset">
            <Field label="HS tariff code" hint="The heading follows the metal, so changing the metal type may change it.">
              <input value={d.hsCode} disabled={readOnly} onChange={(e) => set('hsCode', e.target.value)} />
            </Field>
            <Field label="Country of origin">
              <select value={d.countryOfOrigin} disabled={readOnly} onChange={(e) => set('countryOfOrigin', e.target.value)}>
                <option value="">Not confirmed</option>
                {(countries ?? []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          {d.countryOfOrigin ? null : (
            <Alert tone="us">
              <b>Country of origin is not set.</b> It prints on the customs declaration, so
              a consignment cannot be documented without it. Where Miraki manufactures is
              unconfirmed and is left blank rather than guessed — a guess prints as fact.
            </Alert>
          )}
        
        </Panel>

        {/* CR-48, CR-49 — how the piece is made and what mark it carries. */}
        <Panel title="Make & marking" pad>
          <div className="mk-fset">
            <AttrField
              label="Setting" groups={groups} groupKey={GROUP.setting}
              metalTypeCode={d.metalTypeCode} value={d.settingValueId} disabled={readOnly}
              onChange={(v) => set('settingValueId', v)}
            />
            <AttrField
              label="Finish" groups={groups} groupKey={GROUP.finish}
              metalTypeCode={d.metalTypeCode} value={d.finishValueId} disabled={readOnly}
              onChange={(v) => set('finishValueId', v)}
            />
            <AttrField
              label="Clasp" hint="Bracelets and chains only. Leave unset everywhere else."
              groups={groups} groupKey={GROUP.clasp}
              metalTypeCode={d.metalTypeCode} value={d.claspValueId} disabled={readOnly}
              onChange={(v) => set('claspValueId', v)}
            />
            <AttrField
              label="Earring backing" hint="Earrings only. Leave unset everywhere else."
              groups={groups} groupKey={GROUP.backing}
              metalTypeCode={d.metalTypeCode} value={d.backingValueId} disabled={readOnly}
              onChange={(v) => set('backingValueId', v)}
            />
            <AttrField
              label="Hallmark authority" groups={groups} groupKey={GROUP.hallmark}
              metalTypeCode={d.metalTypeCode} value={d.hallmarkValueId} disabled={readOnly}
              onChange={(v) => set('hallmarkValueId', v)}
            />
          </div>
         
        </Panel>
      </div>

      <Panel
        /* Both halves count the SAME nine-axis combinations now. They did not:
           `missing` came from a three-axis loop while the Generate panel counted
           all nine, so one screen said "9 combinations not created" and "180
           candidates" at once. */
        title={
          missing === 0
            ? `Variants — ${live} in the catalog`
            : `Variants — ${live} in the catalog, ${missing} combination${missing === 1 ? '' : 's'} not created`
        }
        actions={
          !readOnly && product ? (
            <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
              {/* The priced-but-not-created rows, in one request. Typing a
                  price on a row IS proposing it — there is no second place to
                  say which rows you meant, and no list to keep in step. */}
              {pricedDrafts.length > 0 ? (
                <Button
                  size="sm"
                  disabled={createPricedPending}
                  onClick={createPriced}
                >
                  {createPricedPending
                    ? `Creating ${pricedDrafts.length}…`
                    : `Create ${plural(pricedDrafts.length, 'priced variant', 'priced variants')}`}
                </Button>
              ) : null}
              {/* One hand-specified variant, for a piece the axes do not imply. */}
              <Button size="sm" tone="ghost" onClick={() => setCreating(true)}>Add a variant</Button>
              {/* Withdraw, bulked: check rows in the table below, then one click
                  here withdraws all of them — reusing the same single-variant
                  route `setVariantActive` already calls, in sequence. */}
              <Button
                size="sm"
                tone="ghost"
                disabled={selectedVariants.size === 0 || bulkWithdrawPending}
                onClick={bulkWithdraw}
              >
                {bulkWithdrawPending
                  ? `Withdrawing ${selectedVariants.size}…`
                  : `Withdraw${selectedVariants.size > 0 ? ` ${selectedVariants.size}` : ''}`}
              </Button>
            </span>
          ) : null
        }
      >
        {isNew ? (
          /*
           * A variant belongs to a product id, and an unsaved product has none.
           * The generator below renders only once the product exists, so
           * without this the new-product screen showed a matrix of combinations
           * and no means of creating or pricing any of them — which is exactly
           * how it reads as broken rather than as "not yet".
           */
          <Note>
            <b>Price them here, then save.</b> These are the combinations your
            axes imply. Enter a price against every piece this design is actually
            made in and leave the rest blank — saving the design creates exactly
            the rows you priced.
            {pricedDrafts.length > 0 ? (
              <>
                {' '}<b>{plural(pricedDrafts.length, 'row', 'rows')} priced so far.</b>
              </>
            ) : null}
          </Note>
        ) : null}
        {/* Grouped by purity and colour, collapsed. A design with 252 variants
            is not a list anybody reads flat: the question from this screen is
            "what does this come in, and is any of it wrong", and nine groups
            answer it where 252 rows do not. */}
        {rows.length === 0 ? (
          <div className="mk-pbody">
            <Note>
              No variants yet. Choose the purities, colours and carats above, then create them.
            </Note>
          </div>
        ) : (
          <div className="mk-vgroups">
            {[...variantGroups.entries()]
              // Yellow, white, rose — the same order as the image tabs and the
              // list. Insertion order here follows the axis arrays, which are
              // whatever the attribute list returned.
              .sort(([, a], [, b]) => {
                const rank = (c: string | null) =>
                  ['Yellow', 'White', 'Rose'].indexOf(c ?? '') + 1 || 9;
                return (a.purity ?? '').localeCompare(b.purity ?? '') || rank(a.colour) - rank(b.colour);
              })
              .map(([key, g]) => {
              const open = openGroups.has(key);
              const made = g.rows.filter((r) => r.variant);
              // NOT `Number(...).toLocaleString()`, which is what stood here.
              // Two defects in one line: it rounds past 2^53 and it throws away
              // the fourth decimal place NUMERIC(19,4) is keeping, so a group
              // header could disagree with every row inside it. The comparison
              // goes through `packages/money` and the strings are never parsed
              // (NF-04).
              const span = priceSpan(made.map((r) => r.variant!.listPrice));
              // Tri-state: this group's own checkbox reflects the variants
              // WITHIN it, not the screen-wide selection — a "not created"
              // row has nothing to select, so it never counts either way.
              // Withdraw now deletes (below), so there is no longer a
              // withdrawn-but-still-present state to carve out of `made`.
              const groupVariantIds = made.map((r) => r.variant!.id);
              const selectedInGroup = groupVariantIds.filter((id) => selectedVariants.has(id));
              const groupAllSelected = groupVariantIds.length > 0
                && selectedInGroup.length === groupVariantIds.length;
              const groupSomeSelected = selectedInGroup.length > 0 && !groupAllSelected;
              return (
                <div key={key} className="mk-vgroup">
                  <div className="mk-vgroup-head">
                    {readOnly ? null : (
                      <input
                        type="checkbox"
                        checked={groupAllSelected}
                        disabled={groupVariantIds.length === 0}
                        aria-label={`Select all variants in ${g.purity ?? '—'} · ${g.colour ?? 'No colour'}`}
                        ref={(el) => { if (el) el.indeterminate = groupSomeSelected; }}
                        onChange={() => setSelectedVariants((prev) => {
                          const next = new Set(prev);
                          if (groupAllSelected) for (const id of groupVariantIds) next.delete(id);
                          else for (const id of groupVariantIds) next.add(id);
                          return next;
                        })}
                      />
                    )}
                    <button
                      type="button"
                      className="mk-vgroup-toggle"
                      aria-expanded={open}
                      onClick={() => setOpenGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                      })}
                    >
                      <span className="mk-vgroup-tw">{open ? '▾' : '▸'}</span>
                      <b>{g.purity ?? '—'} · {g.colour ?? 'No colour'}</b>
                      <span className="mk-vgroup-n">
                        {made.length} of {g.rows.length}
                        {g.rows.length === made.length ? '' : ' created'}
                      </span>
                      {span ? (
                        <span className="mk-vgroup-p">
                          <Money amount={span.low} currency={product?.currency} />
                          {span.low === span.high
                            ? null
                            : <> – <Money amount={span.high} /></>}
                        </span>
                      ) : null}
                    </button>
                  </div>
                  {open ? (
                    <Table
                      rows={g.rows}
                      columns={matrixColumns}
                      rowKey={(m) => m.key}
                      dense
                      // `table-layout: fixed`, scoped to this table — makes
                      // the percentages below authoritative instead of the
                      // minimums `auto` layout treated them as.
                      className="mk-variance-t"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {/* CR-43 — generate, prune, price. The one place this screen creates
          variants, and it creates them through A-11's import route rather
          than a second bulk path of its own. */}
      {importResult ? (
        <Alert tone={importResult.refused.length > 0 ? 'lapse' : 'them'}>
          <b>
            {importResult.dryRun ? 'Checked, and nothing was written. ' : ''}
            {importResult.variantsCreated}
            {importResult.dryRun ? ' variants would be created' : ' variants created'}
            {importResult.variantsExisting > 0
              ? `, ${importResult.variantsExisting} already in the catalog` : ''}
            {importResult.refused.length > 0
              ? `, ${plural(importResult.refused.length, 'row refused', 'rows refused')}.`
              : '.'}
          </b>
          {importResult.refused.slice(0, 8).map((r) => (
            <div className="mk-dim" key={`${r.kind}/${r.ref}/${r.reason}`}>
              <code>{r.ref}</code> — {r.reason}
            </div>
          ))}
          {importResult.refused.length > 8 ? (
            <div className="mk-dim">and {importResult.refused.length - 8} more.</div>
          ) : null}
        </Alert>
      ) : null}

      {(product || isNew) && !readOnly ? (
        proposal ? (
          <VariantProposalPanel
            proposal={proposal}
            labels={labels}
            // A new design takes the portal's currency when it is saved; until
            // then there is none to name, the same as the priced rows above.
            currency={product?.currency}
            existing={existingByKey}
            canBulk={can('product.bulk')}
            saveFirst={isNew}
            missing={missingForCreate}
            pending={bulkImport.isPending || createProduct.isPending || createPricedPending}
            onChange={setProposal}
            onCreate={runImport}
            onDiscard={() => { setProposal(null); setImportResult(null); }}
          />
        ) : (
          <Panel
            title="Generate variants"
            pad
            actions={
              <Button
                size="sm"
                // Nothing to create is not an action. The count offered is the
                // count that would be written, not the size of the proposal.
                disabled={
                  axes.purityIds.length === 0
                  || candidateTotal > GENERATION_CAP
                  || !activeSet
                  || setWouldCreate === 0
                }
                onClick={startProposal}
              >
                {!activeSet
                  ? 'Generate'
                  : setWouldCreate === 0
                    ? `${activeSet.label} is complete`
                    : `Generate ${setWouldCreate}${activeSet.existing > 0 ? ' new' : ''} for ${activeSet.label}`}
              </Button>
            }
          >
            {/* One metal set at a time — the client's way of working a design:
                10k Rose is generated, priced and created on its own, and no
                other purity or colour is proposed alongside it. */}
            {activeSet && candidateTotal <= GENERATION_CAP ? (
              <Field
                label="Metal set"
                hint={`${setsComplete} of ${plural(metalSets.length, 'metal set', 'metal sets')} complete.`
                  + ' Only the set chosen here is generated and created.'}
              >
                <select
                  value={activeSet.key}
                  onChange={(e) => setChosenSet(e.target.value)}
                >
                  {metalSets.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label} — {m.existing} of {m.total} created{m.existing >= m.total ? ' ✓' : ''}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Note>
              {activeSet ? (
                <>
                  <b>{activeSet.label}</b>: {plural(activeSet.total, 'candidate', 'candidates')}
                  {' '}({plural(size.carats, 'carat', 'carats')}
                  {size.stoneSpecs > 1 ? ` × ${plural(size.stoneSpecs, 'stone specification', 'stone specifications')}` : ''})
                  {activeSet.existing > 0 && setWouldCreate > 0
                    ? `, ${activeSet.existing} already in the catalog, so generating would create ${plural(setWouldCreate, 'variant', 'variants')}.`
                    : '.'}
                </>
              ) : (
                <><b>{plural(candidateTotal, 'candidate', 'candidates')}</b> from {matrixInWords(size)}.</>
              )}
              {' '}Generating proposes them and writes nothing. You then strike out what is not
              made, price the rest — one by one, all at once, or from a column pasted out of the
              workbook — and create them in a single request. Other metal sets are left as they are.
            </Note>
            {activeSet && setWouldCreate === 0 && wouldCreate > 0 ? (
              <Alert tone="them">
                <b>{activeSet.label} is already complete.</b> Choose another metal set above —
                {' '}{plural(metalSets.length - setsComplete, 'set still has', 'sets still have')} variants to create.
              </Alert>
            ) : null}
            {wouldCreate === 0 && axes.purityIds.length > 0 ? (
              <Alert tone="them">
                <b>Every combination these axes imply is already in the catalog.</b> There is
                nothing left to generate. Widen an axis above to offer a combination this design
                is not yet made in.
              </Alert>
            ) : null}
            {axes.purityIds.length === 0 ? (
              <Alert tone="us">
                <b>Choose at least one purity first.</b> Every variant is a purity in a colour at
                a carat; with none chosen there is no metal frame to cross the stone axes against.
              </Alert>
            ) : null}
            {candidateTotal > GENERATION_CAP ? (
              <Alert tone="stop">
                <b>{candidateTotal} is more than this screen will generate.</b> The largest file
                the client has sent is 252 variants, so a number this size is an axis chosen by
                accident rather than a catalog. Narrow the axes above, or load it through A-11.
              </Alert>
            ) : null}
          </Panel>
        )
      ) : null}

      {/* Images belong to the product AND the metal colour, not to the variant:
          a customer choosing rose gold is shown the rose photographs whatever
          carat they pick, so one set covers every purity and carat in a colour.
          A product with no colour axis — anything not gold — has one unkeyed set.

          Laid out the way a customer will see it: a rail of thumbnails against
          one large frame. An administrator checking that the right photograph is
          on the right colour is doing the same thing a buyer does, and a grid of
          equal tiles makes that harder than it needs to be. */}
      <ImagePanel
        images={product?.images ?? []}
        exists={Boolean(product?.id)}
        /* CR-43 — images belong to the metal COLOUR, so the tabs are the colours
           this design is offered in. Derived from the images instead, a product
           with none offered nothing to choose and the first upload of every
           colour was filed as "applies to all of them". */
        offeredColours={allColours
          .filter((c) => axes.colourIds.includes(c.id))
          .map((c) => ({ code: c.code, label: c.label }))}
        readOnly={readOnly}
        pending={deleteImage.isPending || setPrimaryImage.isPending || uploading !== null}
        progress={uploading}
        onRemove={(imageId) => void run(
          () => deleteImage.mutateAsync({ productId: product!.id, imageId }),
        )}
        onSetPrimary={(imageId) => void run(
          () => setPrimaryImage.mutateAsync({ productId: product!.id, imageId }),
        )}
        onAdd={(files, colour) => void addMany(files, colour)}
      />

      {creating && product ? (
        <VariantCreator
          groups={groups}
          metalTypeCode={d.metalTypeCode}
          currency={product.currency}
          pending={createVariant.isPending}
          error={failed}
          onClose={() => { setCreating(false); setFailed(null); }}
          onCreate={(fields) => void run(
            () => createVariant.mutateAsync({
              productId: product.id,
              body: { productId: product.id, ...fields, attributes: {} },
            }),
            () => setCreating(false),
            fields,
            variantFields('Add a variant'),
          )}
        />
      ) : null}

    </>
  );
}

/** A weight, rendered from the string the API sent. Never parsed (NF-04). */
function Weight({ value, unit }: { value: string | null; unit: string }) {
  if (value === null) return <span className="mk-dim">—</span>;
  return <span className="mk-money">{value} {unit}</span>;
}

/** One single-valued attribute picker, showing only what the portal offers (CR-12). */
function AttrField(
  { label, hint, groups, groupKey, metalTypeCode, value, disabled, onChange }: {
    label: string;
    hint?: string;
    groups: AttributeGroup[] | undefined;
    groupKey: string;
    metalTypeCode: string;
    value: string;
    disabled: boolean;
    onChange: (v: string) => void;
  },
) {
  const values = offered(group(groups, groupKey), metalTypeCode);
  return (
    <Field label={label} {...(hint ? { hint } : {})}>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">Not set</option>
        {values.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
      </select>
    </Field>
  );
}

/**
 * One variant, hand-specified and individually priced — Metal only. Stone is
 * set in its own block above and carries no price of its own, so it has no
 * counterpart here.
 */
function VariantCreator(
  { groups, metalTypeCode, currency, pending, error, onClose, onCreate }: {
    groups: AttributeGroup[] | undefined;
    metalTypeCode: string;
    currency: string;
    pending: boolean;
    /** The refusal, shown inside the modal — the page's own alert sits behind it. */
    error: string | null;
    onClose: () => void;
    onCreate: (fields: Record<string, string | null>) => void;
  },
) {
  const purities = offered(group(groups, GROUP.purity), metalTypeCode);
  const colours = offered(group(groups, GROUP.metalColour), metalTypeCode);

  const [f, setF] = useState({
    purityValueId: '', metalColourValueId: '', caratCode: '',
    netMetalWeightG: '', grossWeightG: '', totalCaratWeight: '', listPrice: '',
  });
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });

  const picker = (label: string, value: string, key: keyof typeof f, values: AttributeValue[]) => (
    <Field label={label}>
      <select value={value} onChange={(e) => set(key, e.target.value)}>
        <option value="">Not set</option>
        {values.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
      </select>
    </Field>
  );

  return (
    <Modal
      title="Add a variant"
      onClose={onClose}
      footer={
        <>
          <Button
            size="sm"
            disabled={pending || f.listPrice.trim() === ''}
            onClick={() => onCreate({
              purityValueId: nul(f.purityValueId),
              metalColourValueId: nul(f.metalColourValueId),
              caratCode: nul(f.caratCode),
              netMetalWeightG: nul(f.netMetalWeightG),
              grossWeightG: nul(f.grossWeightG),
              totalCaratWeight: nul(f.totalCaratWeight),
              listPrice: f.listPrice.trim(),
            })}
          >
            Create variant
          </Button>
          <Button size="sm" tone="ghost" onClick={onClose}>Cancel</Button>
        </>
      }
    >
      {error ? <Alert tone="stop"><span className="mk-lines">{error}</span></Alert> : null}
      <Note style={{ marginBottom: 'var(--mk-space-4)' }}>
        One real, priced combination. The code is derived from Purity and
        Colour, and shown once created. Stone is set separately, in its own
        block above — it is descriptive, not a priced axis of this variant.
      </Note>
      <div className="mk-fset">
        {picker('Purity', f.purityValueId, 'purityValueId', purities)}
        {picker('Metal colour', f.metalColourValueId, 'metalColourValueId', colours)}
        <Field label="Centre-stone carat code" hint="e.g. 050 for 0.50ct.">
          <input value={f.caratCode} onChange={(e) => set('caratCode', e.target.value)} />
        </Field>
      </div>
      <div className="mk-fset">
        <Field label="Net metal weight (g)">
          <input value={f.netMetalWeightG} inputMode="decimal" onChange={(e) => set('netMetalWeightG', e.target.value)} />
        </Field>
        <Field label="Gross weight (g)">
          <input value={f.grossWeightG} inputMode="decimal" onChange={(e) => set('grossWeightG', e.target.value)} />
        </Field>
        <Field label="Total carat weight">
          <input value={f.totalCaratWeight} inputMode="decimal" onChange={(e) => set('totalCaratWeight', e.target.value)} />
        </Field>
        <Field label={`List price (${currency})`}>
          <input value={f.listPrice} inputMode="decimal" onChange={(e) => set('listPrice', e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}


/**
 * CR-43 — generate, prune, price. The half of this screen that authors variants.
 *
 * Three things it is careful about:
 *
 *   - **Nothing is written until Create.** The grid is a proposal held in this
 *     component, and Create is the only thing that sends it.
 *   - **Removing a row means the combination IS NOT MADE.** Colette comes in
 *     four purities and three colours, but platinum is not made in yellow or
 *     rose — so two of its twelve frames are struck out and 240 candidates
 *     become 200 real variants. Not unavailable, not out of stock (D4).
 *   - **A price is a string from the input to the request body.** Nothing here
 *     parses one, sums one, or sorts on one.
 */
function VariantProposalPanel(
  { proposal, labels, currency, existing, canBulk, saveFirst, missing, pending, onChange, onCreate, onDiscard }: {
    proposal: Proposal;
    labels: AxisLabels;
    /** Absent for a design not yet saved — the portal's currency is set on save. */
    currency: string | undefined;
    /** Identity keys already in the catalog. Those rows are shown, never sent. */
    existing: ReadonlyMap<string, VariantRow>;
    canBulk: boolean;
    /** A new design: Create saves the product first, then creates the rows. */
    saveFirst: boolean;
    /** Required product details not yet entered; Create waits for none. */
    missing: readonly string[];
    pending: boolean;
    onChange: (next: Proposal) => void;
    onCreate: () => void;
    onDiscard: () => void;
  },
) {
  // Nine frames open at once is readable; two hundred rows is not. Small
  // proposals open themselves, because clicking nine twisties to price nine
  // rows is the kind of ceremony that makes a tool feel hostile.
  const [openFrames, setOpenFrames] = useState<ReadonlySet<string>>(
    () => new Set(proposal.rows.length <= 30 ? proposal.rows.map((r) => r.frameKey) : []),
  );
  const [everyPrice, setEveryPrice] = useState('');
  const [everyWeight, setEveryWeight] = useState<Record<ProposalWeightField, string>>({
    netWeights: '', grossWeights: '', totalCarats: '',
  });
  const [pasteNote, setPasteNote] = useState<string | null>(null);

  const { rows, prices, skus, notMade } = proposal;
  const inCurrency = currency ? ` (${currency})` : '';

  /** One row's draft weight, typed directly or from "Apply to All" below. */
  const setWeightDraft = (field: ProposalWeightField, key: string, v: string) =>
    onChange({ ...proposal, [field]: { ...proposal[field], [key]: v } });

  /** What Create would actually send: kept, and not already a variant. */
  const sendable = rows.filter((r) => !notMade.has(r.key) && !existing.has(r.key));
  const ready = sendable.filter((r) => isPrice(prices[r.key] ?? ''));

  /**
   * Rows that would arrive under the same derived name.
   *
   * A derived code is prefix + purity + colour + carat + THE STONE SEGMENTS, so
   * two rows collide only when they are identical on every axis — which
   * generation cannot produce. The count is kept anyway, and it is not
   * defensive clutter: it was 152 until `zBulkVariant` gained its stone columns,
   * because the bulk path derived a metal-only name and 160 of the client's
   * rows landed on eight of them. If it ever climbs off zero again, the code
   * and the grid have drifted apart and this says so before Create rather than
   * after a refusal list.
   *
   * A supplied SKU still wins over a derived name — that is `zVariantCode`'s
   * rule and the client's workbooks carry their own.
   */
  const unnamedByFrame = new Map<string, number>();
  for (const r of sendable) {
    if ((skus[r.key] ?? '').trim() !== '') continue;
    // The identity the server derives from: the frame AND the stone.
    const derived = [r.frameKey, ...STONE_FIELDS.map((f) => r[f] ?? '')].join('|');
    unnamedByFrame.set(derived, (unnamedByFrame.get(derived) ?? 0) + 1);
  }
  const clashing = [...unnamedByFrame.values()].filter((n) => n > 1);
  const clashRows = clashing.reduce((a, b) => a + b, 0);
  const wouldBeRefused = clashRows - clashing.length;

  const setPrice = (key: string, v: string) => onChange({ ...proposal, prices: { ...prices, [key]: v } });
  const setSku = (key: string, v: string) => onChange({ ...proposal, skus: { ...skus, [key]: v } });
  const setMade = (keys: readonly string[], made: boolean) => {
    const next = new Set(notMade);
    for (const k of keys) { if (made) next.delete(k); else next.add(k); }
    onChange({ ...proposal, notMade: next });
  };

  /**
   * ONE "Apply to All" for the whole toolbar — list price and the three
   * weights all copied to every sendable row in a single action, rather
   * than four separate buttons each requiring its own click. Only the
   * fields the author actually typed a valid value into are applied; an
   * empty or unusable box is left alone rather than blanking every row.
   * Copied, not linked — each row stays its own field afterwards, in the
   * grid below and, once created, in the Variance block's own Edit/Save.
   */
  const anyEveryValueValid = isPrice(cleanPrice(everyPrice))
    || isWeight(cleanWeight(everyWeight.netWeights))
    || isWeight(cleanWeight(everyWeight.grossWeights))
    || isWeight(cleanWeight(everyWeight.totalCarats));

  const applyAllToEvery = () => {
    const priceV = cleanPrice(everyPrice);
    const priceValid = isPrice(priceV);
    const weightValid: Record<ProposalWeightField, string | null> = {
      netWeights: null, grossWeights: null, totalCarats: null,
    };
    for (const field of ['netWeights', 'grossWeights', 'totalCarats'] as const) {
      const v = cleanWeight(everyWeight[field]);
      if (isWeight(v)) weightValid[field] = v;
    }
    if (!priceValid && !weightValid.netWeights && !weightValid.grossWeights && !weightValid.totalCarats) return;

    const nextPrices = { ...prices };
    const nextNet = { ...proposal.netWeights };
    const nextGross = { ...proposal.grossWeights };
    const nextCarat = { ...proposal.totalCarats };
    for (const r of sendable) {
      if (priceValid) nextPrices[r.key] = priceV;
      if (weightValid.netWeights) nextNet[r.key] = weightValid.netWeights;
      if (weightValid.grossWeights) nextGross[r.key] = weightValid.grossWeights;
      if (weightValid.totalCarats) nextCarat[r.key] = weightValid.totalCarats;
    }
    onChange({
      ...proposal, prices: nextPrices,
      netWeights: nextNet, grossWeights: nextGross, totalCarats: nextCarat,
    });

    const applied = [
      priceValid ? `list price ${currency ? `${currency} ` : ''}${priceV}` : null,
      weightValid.netWeights ? `net weight ${weightValid.netWeights}g` : null,
      weightValid.grossWeights ? `gross weight ${weightValid.grossWeights}g` : null,
      weightValid.totalCarats ? `total carat ${weightValid.totalCarats}ct` : null,
    ].filter((s): s is string => s !== null);
    setPasteNote(`${plural(sendable.length, 'row', 'rows')} set to ${applied.join(', ')}.`);
  };

  const columns: Column<Candidate>[] = [
    {
      header: 'Stone',
      render: (c) => {
        const l = stoneLabel(c, labels);
        return l ? <span>{l}</span> : <span className="mk-dim">No stone</span>;
      },
    },
    {
      header: 'SKU',
      render: (c) => {
        const v = existing.get(c.key);
        if (v) return <code>{v.code}</code>;
        if (notMade.has(c.key)) return <span className="mk-dim">—</span>;
        return (
          <input
            value={skus[c.key] ?? ''}
            placeholder="Derived on save"
            aria-label="Supplier SKU"
            onChange={(e) => setSku(c.key, e.target.value)}
          />
        );
      },
    },
    {
      header: `List price${inCurrency}`,
      numeric: true,
      render: (c) => {
        const v = existing.get(c.key);
        if (v) return <Money amount={v.listPrice} />;
        if (notMade.has(c.key)) return <span className="mk-dim">—</span>;
        return (
          <input
            className="mk-num"
            inputMode="decimal"
            aria-label="List price"
            value={prices[c.key] ?? ''}
            onChange={(e) => setPrice(c.key, e.target.value)}
          />
        );
      },
    },
    // Net weight, gross weight, total carat — the same "type it, or Apply to
    // every below" shape as List price, one column per field rather than a
    // loop: `Column` render functions close over `field`, and three explicit
    // columns read the same as the three explicit toolbar fields below them.
    ...(['netWeights', 'grossWeights', 'totalCarats'] as const).map((field): Column<Candidate> => {
      const { label, unit, variantField } = PROPOSAL_WEIGHT_META[field];
      return {
        header: `${label} (${unit})`,
        numeric: true,
        render: (c) => {
          const v = existing.get(c.key);
          if (v) {
            const stored = v[variantField];
            return <Weight value={stored === null ? null : trimAmount(stored)} unit={unit} />;
          }
          if (notMade.has(c.key)) return <span className="mk-dim">—</span>;
          return (
            <input
              className="mk-num"
              inputMode="decimal"
              aria-label={label}
              value={proposal[field][c.key] ?? ''}
              onChange={(e) => setWeightDraft(field, c.key, e.target.value)}
            />
          );
        },
      };
    }),
    {
      header: 'State',
      render: (c) => {
        if (existing.has(c.key)) return <Chip tone="done">In the catalog</Chip>;
        if (notMade.has(c.key)) return <Chip tone="lapse">Not made</Chip>;
        const p = prices[c.key] ?? '';
        if (p === '') return <Chip tone="run">Needs a price</Chip>;
        if (!isPrice(p)) return <Chip tone="stop">Not a price</Chip>;
        return <Chip tone="us">Ready</Chip>;
      },
    },
    {
      header: '',
      numeric: true,
      render: (c) => (existing.has(c.key) ? null : (
        <Button size="sm" tone="ghost" onClick={() => setMade([c.key], notMade.has(c.key))}>
          {notMade.has(c.key) ? 'Restore' : 'Not made'}
        </Button>
      )),
    },
  ];

  const frames = new Map<string, Candidate[]>();
  for (const r of rows) {
    const list = frames.get(r.frameKey);
    if (list) list.push(r); else frames.set(r.frameKey, [r]);
  }

  return (
    <Panel
      title={`Proposed variants${proposal.metalSetLabel ? ` — ${proposal.metalSetLabel}` : ''} — ${rows.length} candidate${rows.length === 1 ? '' : 's'}`}
      actions={
        <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)', flexWrap: 'wrap' }}>
          <Button
            size="sm"
            disabled={
              pending || !canBulk || missing.length > 0
              || ready.length === 0 || ready.length !== sendable.length
            }
            onClick={onCreate}
          >
            {saveFirst ? `Save and create ${ready.length}` : `Create ${ready.length}`}
          </Button>
          <Button size="sm" tone="ghost" disabled={pending} onClick={onDiscard}>Discard</Button>
        </span>
      }
    >
      <div className="mk-pbody">

        {missing.length > 0 ? (
          <Alert tone="us">
            <b>Enter {listInWords(missing)} to create these.</b> A variant belongs to a
            product, so the design is saved first, in the same click.
          </Alert>
        ) : null}

        {wouldBeRefused > 0 ? (
          <Alert tone="lapse">
            <b>{clashRows} rows share {plural(clashing.length, 'derived code', 'derived codes')}.</b>{' '}
            A code is derived from the prefix, purity, colour, carat and stone, so two rows can only
            collide when they are identical on every axis — {wouldBeRefused} of them would be refused
            as duplicates. Enter the SKU the workbook already carries, or change what differs.
            Codes are never invented here.
          </Alert>
        ) : null}

        {!canBulk ? (
          <Alert tone="us">
            Creating variants in bulk needs the <code>product.bulk</code> permission, which this
            account does not hold. The grid can still be built.
          </Alert>
        ) : null}

        <div className="mk-toolbar">
          <Field label={`Price every row${inCurrency}`} >
            <input
              value={everyPrice}
              inputMode="decimal"
              onChange={(e) => setEveryPrice(e.target.value)}
            />
          </Field>
          <Field label={`${PROPOSAL_WEIGHT_META.netWeights.label} every row (${PROPOSAL_WEIGHT_META.netWeights.unit})`}>
            <input
              value={everyWeight.netWeights}
              inputMode="decimal"
              onChange={(e) => setEveryWeight((prev) => ({ ...prev, netWeights: e.target.value }))}
            />
          </Field>
          <Field label={`${PROPOSAL_WEIGHT_META.grossWeights.label} every row (${PROPOSAL_WEIGHT_META.grossWeights.unit})`}>
            <input
              value={everyWeight.grossWeights}
              inputMode="decimal"
              onChange={(e) => setEveryWeight((prev) => ({ ...prev, grossWeights: e.target.value }))}
            />
          </Field>
          <Field label={`${PROPOSAL_WEIGHT_META.totalCarats.label} every row (${PROPOSAL_WEIGHT_META.totalCarats.unit})`}>
            <input
              value={everyWeight.totalCarats}
              inputMode="decimal"
              onChange={(e) => setEveryWeight((prev) => ({ ...prev, totalCarats: e.target.value }))}
            />
          </Field>
          {/* ONE common action for the whole toolbar — whichever of the four
              boxes above actually hold a usable value are copied to every
              sendable row in this one click; an untouched or unusable box
              is left alone rather than blanking a field nobody meant to
              touch. */}
          <Button size="sm" tone="ghost" disabled={!anyEveryValueValid} onClick={applyAllToEvery}>
            Apply to all {sendable.length}
          </Button>
        </div>
        {pasteNote ? <Note style={{ marginTop: 'var(--mk-space-3)' }}>{pasteNote}</Note> : null}
      </div>

      <div className="mk-vgroups">
        {[...frames.entries()].map(([frameKey, frameRows]) => {
          const head = frameRows[0];
          if (!head) return null;
          const open = openFrames.has(frameKey);
          const keys = frameRows.map((r) => r.key);
          const allNotMade = frameRows.every((r) => notMade.has(r.key));
          const span = priceSpan(frameRows.map((r) => prices[r.key] ?? ''));
          const made = frameRows.filter((r) => !notMade.has(r.key)).length;
          return (
            <div key={frameKey} className="mk-vgroup">
              <div style={{ display: 'flex', alignItems: 'center', paddingInlineEnd: 'var(--mk-space-3)' }}>
                <button
                  type="button"
                  className="mk-vgroup-head"
                  aria-expanded={open}
                  onClick={() => setOpenFrames((prev) => {
                    const next = new Set(prev);
                    if (next.has(frameKey)) next.delete(frameKey); else next.add(frameKey);
                    return next;
                  })}
                >
                  <span className="mk-vgroup-tw">{open ? '▾' : '▸'}</span>
                  <b>{frameLabel(head, labels)}</b>
                  <span className="mk-vgroup-n">
                    {allNotMade ? 'Not made' : `${made} of ${frameRows.length}`}
                  </span>
                  {span ? (
                    <span className="mk-vgroup-p">
                      <Money amount={span.low} currency={currency} />
                      {span.low === span.high ? null : <> – <Money amount={span.high} /></>}
                    </span>
                  ) : null}
                </button>
                <Button size="sm" tone="ghost" onClick={() => setMade(keys, allNotMade)}>
                  {allNotMade ? 'Restore frame' : 'Not made'}
                </Button>
              </div>
              {open ? <Table rows={frameRows} columns={columns} rowKey={(r) => r.key} dense /> : null}
            </div>
          );
        })}
      </div>

    </Panel>
  );
}

// ------------------------------------------------------------ certifications

/** D5 — which bodies Miraki uses is still open; these are the two the client named. */
const CERT_BODIES = ['GIA', 'IGI'] as const;
const CERT_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
/** Mirrors the server's cap (`addCertificateAttachment`); checked here so a person is told at once. */
const CERT_MAX_BYTES = 10 * 1024 * 1024;
/** Documents in flight at once. Each upload is several round trips to the database, so
 *  they overlap; more than a few at a time only queues behind the connection pool. */
const CERT_UPLOAD_PARALLEL = 3;

interface CertDraft {
  rid: string;
  body: string;
  variantCode: string;
  certificateNumber: string;
}

/**
 * A document chosen for a certification but not yet on the server.
 * `waiting` — chosen, goes up on Save. `uploading` — on its way. `failed` — the
 * upload was refused or lost; the certification itself is saved regardless, and
 * this one can be retried on its own.
 */
interface PendingDoc {
  key: string;
  rid: string;
  file: File;
  state: 'waiting' | 'uploading' | 'failed';
}

function ClipIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

/** A file picker behind a button — the native input is hidden, the button opens it. */
function AttachButton({ onPick }: { onPick: (files: FileList) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        accept={CERT_ACCEPT}
        hidden
        aria-label="Certificate documents"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onPick(e.target.files);
          // Cleared so choosing the same file again still fires a change.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        className="mk-clip-add"
        aria-label="Attach certificate"
        title="Attach certificate"
        onClick={() => ref.current?.click()}
      >
        +
      </button>
    </>
  );
}

/**
 * The certifications recorded against a product: which body issued each, which
 * variant it is for, its number, and the certificate documents themselves.
 *
 * A variant code can carry several documents, and each shows as a paperclip —
 * click to open it. The certification fields save on their own and quickly;
 * documents go up afterwards, in the background, so a slow or failed upload
 * never holds the rest of the save. The row id is what ties a document to its
 * certification, and it is sent back on every save so editing a row never
 * orphans what was attached to it.
 */
function CertificationsPanel(
  { certifications, attachments, variantCodes, readOnly, pending,
    onSave, onUpload, onRemoveAttachment, onOpenAttachment }: {
    certifications: CertificateRuleRow[];
    attachments: CertificateAttachment[] | undefined;
    /** The product's own variant codes, offered as suggestions — any code may still be typed. */
    variantCodes: string[];
    readOnly: boolean;
    pending: boolean;
    /**
     * Resolves on a genuine save, rejects on a refusal — the draft below
     * only clears on the former. `void`-returning would leave no way to
     * tell the two apart, which is the actual reason Save looked broken:
     * the request landed, but the local draft never let go of what was
     * typed, so the screen looked exactly the same before and after,
     * whether the save had happened or not.
     */
    onSave: (body: CertificateRuleBody[]) => Promise<unknown>;
    onUpload: (certificateId: string, file: File) => Promise<unknown>;
    onRemoveAttachment: (attachment: CertificateAttachment) => Promise<unknown>;
    onOpenAttachment: (attachment: CertificateAttachment) => Promise<unknown>;
  },
) {
  const [edit, setEdit] = useState<CertDraft[] | null>(null);
  const [docs, setDocs] = useState<PendingDoc[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const listId = useId();

  const rows: CertDraft[] = edit ?? certifications.map((r) => ({
    rid: r.id, body: r.body, variantCode: r.appliesWhen, certificateNumber: r.scope,
  }));
  const anyFailed = docs.some((d) => d.state === 'failed');
  const dirty = edit !== null || docs.some((d) => d.state === 'waiting');
  const valid = rows.every((r) => r.body.trim() !== '' && r.variantCode.trim() !== '' && r.certificateNumber.trim() !== '');

  const patch = (rid: string, p: Partial<CertDraft>) =>
    setEdit(rows.map((r) => (r.rid === rid ? { ...r, ...p } : r)));

  const addFiles = (rid: string, list: FileList) => {
    const accepted: PendingDoc[] = [];
    let refused = false;
    for (const f of Array.from(list)) {
      if (!CERT_ACCEPT.split(',').includes(f.type) || f.size > CERT_MAX_BYTES) refused = true;
      else accepted.push({ key: crypto.randomUUID(), rid, file: f, state: 'waiting' });
    }
    if (accepted.length > 0) setDocs((prev) => [...prev, ...accepted]);
    setNote(refused ? 'Only PDF or image files up to 10 MB can be attached.' : null);
  };

  /** Up to `CERT_UPLOAD_PARALLEL` at once; one that fails is marked, and the rest carry on. */
  const upload = async (batch: PendingDoc[]) => {
    const queue = [...batch];
    const worker = async () => {
      for (let d = queue.shift(); d !== undefined; d = queue.shift()) {
        try {
          await onUpload(d.rid, d.file);
          setDocs((prev) => prev.filter((x) => x.key !== d.key));
        } catch {
          const key = d.key;
          setDocs((prev) => prev.map((x) => (x.key === key ? { ...x, state: 'failed' } : x)));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CERT_UPLOAD_PARALLEL, batch.length) }, worker));
  };

  const save = async () => {
    setNote(null);
    // Nothing to send when only documents were added — the certifications are unchanged.
    if (edit !== null) {
      setSaving(true);
      try {
        await onSave(edit.map((r) => ({
          id: r.rid,
          body: r.body.trim(),
          appliesWhen: r.variantCode.trim(),
          scope: r.certificateNumber.trim(),
        })));
      } catch {
        // The refusal is already on screen (the parent's banner); the draft stays to be fixed.
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    // Documents for a certification that was just removed have nowhere to go.
    const alive = new Set(rows.map((r) => r.rid));
    const batch = docs.filter((d) => d.state === 'waiting' && alive.has(d.rid));
    setEdit(null);
    setDocs((prev) => prev
      .filter((d) => alive.has(d.rid))
      .map((d) => (batch.some((b) => b.key === d.key) ? { ...d, state: 'uploading' } : d)));
    // Not awaited: the certifications are saved, the person can carry on.
    void upload(batch);
  };

  const retry = (d: PendingDoc) => {
    setDocs((prev) => prev.map((x) => (x.key === d.key ? { ...x, state: 'uploading' } : x)));
    void upload([d]);
  };

  const removeSaved = async (a: CertificateAttachment) => {
    setNote(null);
    try { await onRemoveAttachment(a); } catch { setNote('That document could not be removed.'); }
  };

  const openSaved = async (a: CertificateAttachment) => {
    setNote(null);
    try { await onOpenAttachment(a); } catch { setNote('That document could not be opened.'); }
  };

  const columns: Column<CertDraft>[] = [
    {
      header: 'Certification',
      render: (r) => (
        <select
          className="mk-fld"
          value={r.body}
          disabled={readOnly}
          aria-label="Certification"
          onChange={(e) => patch(r.rid, { body: e.target.value })}
        >
          {/* A row saved before the list was narrowed to GIA and IGI still shows what it holds. */}
          {(CERT_BODIES as readonly string[]).includes(r.body) ? null : <option value={r.body}>{r.body}</option>}
          {CERT_BODIES.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      ),
    },
    {
      header: 'Variant Code',
      render: (r) => (
        <input
          className="mk-fld"
          value={r.variantCode}
          list={listId}
          placeholder="Variant Code"
          aria-label="Variant Code"
          disabled={readOnly}
          onChange={(e) => patch(r.rid, { variantCode: e.target.value })}
        />
      ),
    },
    {
      header: 'Certificate Number',
      render: (r) => (
        <input
          className="mk-fld"
          value={r.certificateNumber}
          placeholder="Certificate Number"
          aria-label="Certificate Number"
          disabled={readOnly}
          onChange={(e) => patch(r.rid, { certificateNumber: e.target.value })}
        />
      ),
    },
    {
      header: 'Attach Certificate',
      render: (r) => {
        const saved = (attachments ?? []).filter((a) => a.certificateId === r.rid);
        const chosen = docs.filter((d) => d.rid === r.rid);
        return (
          <div className="mk-clips">
            {saved.map((a, i) => (
              <span key={a.id} className="mk-clip-wrap">
                <button
                  type="button"
                  className="mk-clip"
                  aria-label={`View certificate ${i + 1}`}
                  title="View certificate"
                  onClick={() => void openSaved(a)}
                >
                  <ClipIcon />
                </button>
                {readOnly ? null : (
                  <button
                    type="button"
                    className="mk-clip-x"
                    aria-label={`Remove certificate ${i + 1}`}
                    title="Remove certificate"
                    onClick={() => void removeSaved(a)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {chosen.map((d) => (
              <span
                key={d.key}
                className={`mk-clip-wrap ${d.state === 'failed' ? 'mk-clip-failed' : d.state === 'uploading' ? 'mk-clip-busy' : 'mk-clip-wait'}`}
                aria-busy={d.state === 'uploading'}
              >
                <button
                  type="button"
                  className="mk-clip"
                  aria-label={d.state === 'failed' ? 'Retry upload' : 'Certificate attached'}
                  title={d.state === 'failed' ? 'Retry upload' : d.state === 'uploading' ? 'Uploading' : 'Uploads when you save'}
                  disabled={d.state !== 'failed'}
                  onClick={() => retry(d)}
                >
                  <ClipIcon />
                </button>
                {d.state === 'uploading' ? null : (
                  <button
                    type="button"
                    className="mk-clip-x"
                    aria-label="Remove certificate"
                    title="Remove certificate"
                    onClick={() => setDocs((prev) => prev.filter((x) => x.key !== d.key))}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {readOnly ? null : <AttachButton onPick={(list) => addFiles(r.rid, list)} />}
          </div>
        );
      },
    },
    {
      header: '',
      numeric: true,
      render: (r) => (readOnly ? null : (
        <Button
          size="sm"
          tone="ghost"
          onClick={() => setEdit(rows.filter((x) => x.rid !== r.rid))}
        >
          Remove
        </Button>
      )),
    },
  ];

  const message = anyFailed ? 'Some attachments could not be uploaded. You can retry them.' : note;

  return (
    <Panel
      title={`Certifications — ${rows.length}`}
      actions={readOnly ? null : (
        <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)' }}>
          <Button
            size="sm"
            tone="ghost"
            onClick={() => setEdit([...rows, {
              rid: crypto.randomUUID(), body: 'GIA', variantCode: '', certificateNumber: '',
            }])}
          >
            Add certification
          </Button>
          <Button
            size="sm"
            disabled={!dirty || !valid || saving || pending}
            // The draft (`edit`) only clears once the save actually lands —
            // on a refusal the promise rejects, and whatever was typed is
            // still there to fix and retry.
            onClick={() => void save()}
          >
            Save
          </Button>
        </span>
      )}
    >
      <datalist id={listId}>
        {variantCodes.map((c) => <option key={c} value={c} />)}
      </datalist>
      {message ? <div className="mk-pbody"><Note>{message}</Note></div> : null}
      {rows.length === 0 ? null : (
        <Table rows={rows} columns={columns} rowKey={(r) => r.rid} dense />
      )}
    </Panel>
  );
}
